import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowsClockwise,
    CaretDown,
    Check,
    Cube,
    CurrencyCircleDollar,
    Lightning,
    ListBullets,
    MapPin,
    Plug,
    Star,
    Sparkle,
    Sun,
    X,
} from '@phosphor-icons/react';
import { notification, Spin, Tooltip, Modal } from 'antd';
import GenerateQuoteModal, { QuoteFormState } from './GenerateQuoteModal';
import { useSystemDesignMissingItems } from './useSystemDesignMissingItems';
import {
    hasQuoteHardMissForProducts,
    quoteBlockedReasonFromItems,
} from './systemDesignMissingItems';
import type { QuoteProductType, SystemDesignMissingItem } from './systemDesignMissingItems';
import { SolarQuoteFormState } from './SolarQuoteDetails';
import DashboardModal from './DashboardModal';
import './oneButton-css/EditorDashboardShell.css';
import './oneButton-css/FinancingSystemHeader.css';
import './oneButton-css/FinancingAiPlansView.css';
import { FinancingCombinedView, FinancingDealHealthChip } from './FinancingCombinedView';
import {
    financePartnerKeysEqual,
    formatSolarRatePerKwh,
    normalizePartner,
    monthlyFromOffer,
    solarRateFromOffer,
    termAprNumbersFromOffer,
} from './utils/finance';
import { FinancingAiPlansView } from './FinancingAiPlansView';
import type { FinancingAiQuoteContext } from './FinancingAiPlansView';
import { buildFinancingAiPlans, resolveUtilityBillMonthly } from './financingAiPlansData';
import type { FinancingAiPlan } from './financingAiPlansData';
import {
    buildRecommendedCombinedFinancing,
    filterCombinedSummaryByLineIds,
    type CombinedFinancingSummary,
} from './financingCombinedSummary';
import {
    atomicProductTypesFromCombinedSummary,
    buildSelectedQuoteFromCombinedSummary,
    buildSelectedQuoteFromOpportunityProductTypes,
    findCombinedSummaryOpportunityMismatches,
    type CombinedQuoteMatchIssue,
} from './buildGenerateQuoteFromFinanceLine';
import { OBD_DASHBOARD_MODAL_Z } from './oneButtonOverlayZ';
import { useSunnyFocusedSectionOpen } from './useSunnyFocusedSectionOpen';

import {
    buildPricingDesignFingerprint,
    clearPricingSessionCache,
    computeIncrementalPricingFetch,
    filterFinanceGridByEnabledProducts,
    getPricingSessionCacheForFingerprint,
    replacePricingSessionCache,
    upsertPricingSessionCache,
} from '../../../../helpers/pricingSessionCache';
import { pricingPayloadRequiresSolarMetrics, pricingProductTypeNames, filterPricingProductsForApi } from '../../../../helpers/financeSelectors';
import {
    batteryDesignSourceLabel,
    countDesignArrayModules,
    opportunityNeedsBatteryDesign,
    opportunityNeedsRoofSquares,
    resolveBatteryDesignForPricing,
    resolveRoofSquaresWithFetch,
    roofSquaresSourceLabel,
    sumOneDrawDesignListSquares,
    sumOneDrawRoofSquareTable,
    validatePricingProductFallbacks,
} from '../../../../helpers/productDesignFallbacks';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import { effectiveAnnualUsageKwhForPricing } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import {
    annualProductionKwhFromSnapshot,
    isFinalizedProductsSolarPricingDesign,
    parseDesignDataRecord,
    resolveSolarAnnualProductionKwh,
    systemSizeKwFromSnapshot,
} from '../../../../helpers/systemDesignSolarMetrics';
import { useViewerStore } from '../../../../store/store';
import {
    useOneButtonStore,
    COMMON_CONSTANTS,
    type FinancePartnerRow,
    type FinancingProduct,
    type PriceCell,
    type SelectedDesignForPricing,
    type PartnerSyncChecklist,
} from '../../../../store/oneButtonStore';
import { resolveUtilityCompanyCode } from './utils/finance';
import { partnerRequiresQuoteSyncChecklist } from './partnerSyncConfig';
import {
    atomicProductTypesFromFinancingProduct,
    buildQuoteSyncScopeFromCombinedSummary,
    evaluateGenerateQuoteSyncGate,
    findFirstIncompleteQuoteSyncTarget,
    type GenerateQuoteSyncScope,
} from './generateQuoteSyncGate';
import { isStorageFinanceMethod, lightreachSolarBatteryRequireSingleQuote } from './productsFinanceHubHelpers';
import { useOptionalProductsFinanceHubContext } from './ProductsFinanceHubContext';
import {
    defaultQuoteSyncTarget,
    openPartnerSyncForQuoteTarget,
    productTypeFromFinancingProduct,
    type QuoteSyncTarget,
} from './openPartnerSyncFromQuoteBlock';
import {
    evaluateDesignLossesMismatchForQuote,
    showDesignLossesMismatchModal,
} from './designLossesFinanceGate';
import {
    showFinanceMethodMismatchModal,
    showFinanceMethodMismatchedModal,
    showFinancePartnerMismatchModal,
    showFinancePartnerSyncRequiredModal,
    showInvalidQuoteCombinationModal,
    showNoRecommendedPricingModal,
    showPartnerSyncRequiredModal,
    showPricingNotFoundModal,
    showProductNotSelectedModal,
    showUnableToOpenQuoteFormModal,
} from './quoteValidationModals';

/** Grid columns: product types in this order */
const FINANCING_PRODUCTS: FinancingProduct[] = [
    'Solar',
    'Roof',
    'Solar + Roof',
    'Battery',
    'Battery + Roof',
    'Solar + Battery',
];

/** Default partner order (must match {@link normalizePartner} output for merge to work). */
const DEFAULT_FINANCE_PARTNERS = ['Cash', 'GoodLeap', 'IGS', 'LightReach', 'Enfin'];

/** Human-readable labels for the financing grid / view-all table only. */
const PARTNER_GRID_LABELS: Record<string, string> = {
    Cash: 'Cash',
    GoodLeap: 'Good Leap',
    IGS: 'IGS',
    LightReach: 'Lightreach',
    Enfin: 'Enfin',
};

/** Split "6,665 kWh (finalized design)" into value + short source badge. */
function splitFinancingProductionLabel(raw: string): { value: string; note: string | null } {
    const text = String(raw ?? '—').trim();
    const match = text.match(/^(.+?)\s*(\([^)]+\))\s*$/);
    if (!match) return { value: text || '—', note: null };
    const note = match[2].slice(1, -1).trim();
    return { value: match[1].trim() || '—', note: note || null };
}

function formatFinancingProductionNote(note: string): string {
    if (!note) return note;
    return note.charAt(0).toUpperCase() + note.slice(1);
}

/** Shown in grid cells when a partner has no pricing for a product column. */
const FINANCING_CELL_NA_LABEL = 'N/A';

function partnerLabelForGrid(partner: string): string {
    return PARTNER_GRID_LABELS[partner] ?? partner;
}

const TOOLTIP_ESCALATOR = 'Your monthly payment may increase by this percentage each year.';
const TOOLTIP_PROD_GUARANTEE = 'Lease includes a guarantee on system production.';

const FINANCE_METHOD_LABELS: Record<string, string> = {
    LOAN: 'Loan',
    LEASE: 'Lease',
    PPA: 'PPA',
    LEASEPRODGUAR: 'Lease (Prod Guarantee)',
};

function financeMethodToLabel(method: string): string {
    const u = (method || '').toUpperCase();
    return FINANCE_METHOD_LABELS[u] || method;
}

/** Tie-break for same monthly/total: PPA > LEASE > LOAN > other (Cash = 0). */
function financeMethodTierFromLabel(display: string | undefined, isCash: boolean): number {
    if (isCash) return 0;
    const s = (display || '').toLowerCase();
    if (s.includes('ppa')) return 3;
    if (s.includes('lease')) return 2;
    if (s.includes('loan')) return 1;
    return 0;
}

function partnerRowOrderIndex(partner: string): number {
    const i = DEFAULT_FINANCE_PARTNERS.indexOf(partner);
    return i === -1 ? 999 : i;
}

/** Best-pricing grid prefers a 2.99% escalator over a cheaper 0% estimate. */
const PREFERRED_ESCALATOR_PCT = 2.99;

function isPreferredEscalatorPct(escalator: number | null | undefined): boolean {
    if (escalator == null || !Number.isFinite(escalator)) return false;
    return Math.abs(escalator - PREFERRED_ESCALATOR_PCT) < 0.05;
}

type RecommendedSortKey = {
    preferredEscalator: boolean;
    sortKey: number;
    tier: number;
    partner: string;
};

function compareRecommendedSortKeys(a: RecommendedSortKey, b: RecommendedSortKey): number {
    if (a.preferredEscalator !== b.preferredEscalator) return a.preferredEscalator ? -1 : 1;
    if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
    if (a.tier !== b.tier) return b.tier - a.tier;
    return partnerRowOrderIndex(a.partner) - partnerRowOrderIndex(b.partner);
}

/**
 * Main grid: at most one "recommended" cell per product column — 2.99% escalator first,
 * then lowest payment, then PPA > LEASE > LOAN. Cash uses total (stored in cell.monthly).
 */
function applySingleRecommendedPerProductColumn(rows: FinancePartnerRow[]): void {
    for (const product of FINANCING_PRODUCTS) {
        type Cand = RecommendedSortKey & { rowIndex: number };
        const candidates: Cand[] = [];
        rows.forEach((row, rowIndex) => {
            const cell = row.prices[product];
            if (!cell) return;
            const isCash = row.partner === 'Cash';
            const sortKey = cell.monthly;
            if (!Number.isFinite(sortKey) || sortKey <= 0) return;
            const tier = financeMethodTierFromLabel(cell.financeMethod, isCash);
            candidates.push({
                rowIndex,
                sortKey,
                tier,
                partner: row.partner,
                preferredEscalator: isPreferredEscalatorPct(cell.escalator),
            });
        });
        rows.forEach((row) => {
            const c = row.prices[product];
            if (c) c.recommended = false;
        });
        if (candidates.length === 0) continue;
        candidates.sort(compareRecommendedSortKeys);
        const cell = rows[candidates[0].rowIndex].prices[product];
        if (cell) cell.recommended = true;
    }
}

/** View-all modal: same single-winner rule per product (uses monthly or Cash loan/total). */
function applySingleRecommendedViewAllRows(rows: PricingViewAllRow[]): void {
    const byProduct = new Map<FinancingProduct, PricingViewAllRow[]>();
    for (const r of rows) {
        const pk = normalizeProductType(r.product);
        if (!pk || !FINANCING_PRODUCTS.includes(pk)) continue;
        const list = byProduct.get(pk) ?? [];
        list.push(r);
        byProduct.set(pk, list);
    }
    for (const product of FINANCING_PRODUCTS) {
        const group = byProduct.get(product);
        if (!group?.length) continue;
        group.forEach((r) => {
            r.recommended = false;
        });
        type Cand = RecommendedSortKey & { row: PricingViewAllRow };
        const candidates: Cand[] = [];
        for (const r of group) {
            if (r.error) continue;
            const isCash = r.partner === 'Cash';
            const amt = isCash ? (r.loanAmount ?? r.monthly ?? 0) : (r.monthly ?? 0);
            if (!Number.isFinite(amt) || amt <= 0) continue;
            candidates.push({
                row: r,
                sortKey: amt,
                tier: financeMethodTierFromLabel(r.financeMethod, isCash),
                partner: r.partner,
                preferredEscalator: isPreferredEscalatorPct(r.escalatorPct),
            });
        }
        if (candidates.length === 0) continue;
        candidates.sort(compareRecommendedSortKeys);
        candidates[0].row.recommended = true;
    }
}

/** Normalize API ProductType to grid product key. Solar + Battery + Roof is not shown as a column. */
function normalizeProductType(productType: string): FinancingProduct | null {
    const t = (productType || '').trim();
    if (!t) return null;
    const lower = t.toLowerCase().replace(/\s+/g, '');
    // Exact compound matches (Solar + Battery + Roof excluded from grid — return null)
    if (lower === 'solarbatteryroof' || lower === 'solar+battery+roof') return null;
    if (lower === 'solarbattery') return 'Solar + Battery';
    if (lower === 'solarroof') return 'Solar + Roof';
    if (lower === 'batteryroof') return 'Battery + Roof';
    if (lower === 'solar+battery') return 'Solar + Battery';
    if (lower === 'solar+roof') return 'Solar + Roof';
    if (lower === 'battery+roof') return 'Battery + Roof';
    // Single product types
    if (lower === 'solar') return 'Solar';
    if (lower === 'roof') return 'Roof';
    if (lower === 'battery') return 'Battery';
    if (FINANCING_PRODUCTS.includes(t as FinancingProduct)) return t as FinancingProduct;
    return null;
}

/** New API: array of { FinancePartner, pricing: [{ ProductType, FinanceMethod, Offer: [{ payload?, __error?, Recommended }] }] } */
export interface PricingPartnerBlock {
    FinancePartner: string;
    pricing: Array<{
        ProductType?: string;
        FinanceMethod?: string;
        financeMethodName?: string;
        Offer?: Array<{ payload?: any; __error?: string; Recommended?: boolean;[k: string]: any }>;
        tier?:string
    }>;
}

/** Extract loan amount or total cost for display (CASH offers use totalCost) */
function loanAmountFromOffer(offer: any): number | null {
    if (offer?.__error) return null;
    const payload = offer?.payload;
    if (Array.isArray(payload) && payload[0]) {
        const la = payload[0]?.loanAmount ?? payload[0]?.LoanAmount;
        const v = la?.value ?? la?.Value;
        if (typeof v === 'number') return v;
    }
    if (typeof offer?.totalCost === 'number') return offer.totalCost;
    return null;
}

/** Solar-only EPC / purchase component on PPA & lease offers (several API spellings). */
function solarOnlyEpcPaymentFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;
    const read = (o: any): unknown =>
        o?.solarOnlyEpcPayment ?? o?.SolarOnlyEpcPayment ?? o?.solar_only_epc_payment;
    let v: unknown = read(offer);
    if (v == null || v === '') v = read(offer?.ExcelOffer);
    if ((v == null || v === '') && offer?.payload != null) {
        const p = offer.payload;
        if (Array.isArray(p) && p[0]) v = read(p[0]) ?? read(p[0]?.ExcelOffer);
        else if (typeof p === 'object') v = read(p) ?? read((p as any).ExcelOffer);
    }
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = parseFloat(v.replace(/[$,]/g, ''));
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

/** GoodLeap PPA: payload.pricing[].purchasePrice.value when API does not set solarOnlyEpcPayment. */
function goodLeapPayloadPricingPurchasePriceFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;

    const readFromLine = (line: any): number | null => {
        if (!line || typeof line !== 'object') return null;
        const pp = line.purchasePrice ?? line.PurchasePrice;
        const v = pp?.value ?? pp?.Value;
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
        if (typeof v === 'string' && v.trim() !== '') {
            const n = parseFloat(v.replace(/[$,]/g, ''));
            return Number.isFinite(n) && n > 0 ? n : null;
        }
        return null;
    };

    const pricingFrom = (row: any): any[] | null => {
        if (!row || typeof row !== 'object') return null;
        const pr = row.pricing ?? row.Pricing;
        return Array.isArray(pr) ? pr : null;
    };

    const payload = offer.payload;
    if (Array.isArray(payload)) {
        for (const row of payload) {
            const pr = pricingFrom(row);
            if (!pr) continue;
            for (const line of pr) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
    } else if (payload && typeof payload === 'object') {
        const top = pricingFrom(payload);
        if (top) {
            for (const line of top) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
        for (const val of Object.values(payload)) {
            if (!val || typeof val !== 'object') continue;
            const pr = pricingFrom(val);
            if (!pr) continue;
            for (const line of pr) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
    }
    return null;
}

/** Single “purchase” figure for GoodLeap UI: loan, EPC, or cash total depending on finance method. */
function goodLeapPurchasePriceFromOffer(offer: any, financeMethodRaw: string): number | null {
    if (!offer || offer.__error) return null;
    const m = (financeMethodRaw || '').toUpperCase().replace(/\s+/g, '');
    if (m === 'CASH') {
        if (typeof offer.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
            return offer.totalCost;
        }
        return null;
    }
    if (m === 'LOAN') {
        return loanAmountFromOffer(offer);
    }
    if (m === 'PPA' || m === 'LEASE' || m.startsWith('LEASE')) {
        const purchase = goodLeapPayloadPricingPurchasePriceFromOffer(offer);
        if (purchase != null && purchase > 0) return purchase;
        const epc = solarOnlyEpcPaymentFromOffer(offer);
        if (epc != null && epc > 0) return epc;
        const loan = loanAmountFromOffer(offer);
        return loan != null && loan > 0 ? loan : null;
    }
    const loan = loanAmountFromOffer(offer);
    if (loan != null && loan > 0) return loan;
    const epc = solarOnlyEpcPaymentFromOffer(offer);
    if (epc != null && epc > 0) return epc;
    if (typeof offer.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
        return offer.totalCost;
    }
    return null;
}

/** Escalator % from offer (for PPA/lease) */
function escalatorFromOffer(offer: any): number | null {
    const raw = offer?.ExcelOffer?.escalator ?? offer?.ExcelOffer?.Escalator;
    if (typeof raw !== 'number') return null;
    return raw > 0 && raw < 1 ? +(raw * 100).toFixed(1) : raw;
}

/** Tree cut cost ($) included on some pricing offers (tree-cutting scenarios). */
function treeCutCostFromOffer(offer: any): number | null {
    const raw = offer?.TreeCutCost ?? null;
    if (raw == null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, '').trim());
    return Number.isFinite(n) && n >= 0 ? n : null;
}

export interface PricingViewAllRow {
    partner: string;
    product: string;
    financeMethod: string;
    treeDetailsText?: string;
    treeCutCost: number | null;
    monthly: number | null;
    loanAmount: number | null;
    term: number | null;
    apr: number | null;
    escalatorPct: number | null;
    minimumRate: number | null;
    rateCap: number | null;
    recommended: boolean;
    error: string | null;
}

export interface selectedQuote {
    product: string;
    partner?: string;
    methord?: string;
    price?: number | null;
    ExcelOffer?: any;
    payload?: any;
    term?: number;
    apr?: number;
    /** Escalator % from the selected pricing cell (0 is valid for Lightreach Battery Lease). */
    escalator?: number | null;
    clonedQuoteDetails?:any;
    products?: selectedQuote[];
    productType?: string;
    tier?:string;
    /** Pricing API `Recommended: true` — required to seed Tesla amount from ExcelOffer.batteryEPC. */
    Recommended?: boolean;
}

/** Detect new API shape: array of { FinancePartner, pricing: [...] } */
function isNewPricingFormat(raw: any): boolean {
    if (!Array.isArray(raw) || raw.length === 0) return false;
    const first = raw[0];
    return first && typeof first === 'object' && 'FinancePartner' in first && Array.isArray(first.pricing);
}

/** Matches System metrics / Utility card: monthly-derived or explicit annual, then Salesforce load. */
function resolvedUtilityAnnualKwh(
    base: Record<string, unknown>,
    sfAnnualUsage: number | null | undefined,
): number | null {
    return effectiveAnnualUsageKwhForPricing(base, sfAnnualUsage);
}

function positiveKwFromUnknown(v: unknown): number | null {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** Live 3D / module-graph annual kWh (matches System metrics "Annual prod."). */
function viewerLiveAnnualKwh(viewerActual: number | undefined): number | null {
    if (viewerActual == null) return null;
    const n = Number(viewerActual);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n);
}

/** Live 3D system size kW DC (matches System metrics). */
function viewerLiveSystemKw(viewerTotal: number | undefined): number | null {
    if (viewerTotal == null) return null;
    const n = Number(viewerTotal);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

function isAiLayoutPricingDesign(design: SelectedDesignForPricing | null | undefined): boolean {
    return !!(design?.layoutKey && design.layoutKey !== 'products-solar');
}

/** Tree-cutting $ flow: metrics are passed explicitly; live 3D viewer is a different scenario. */
function isTreeCutLayoutPricingDesign(design: SelectedDesignForPricing | null | undefined): boolean {
    return typeof design?.layoutKey === 'string' && design.layoutKey.startsWith('tree-cut:');
}

/**
 * Inline Financing Options uses finalized design metrics (RetrieveSystemDesign).
 * Modal AI/tree $ uses explicit scenario selection via designForTrigger.
 * Modal products-solar $ also uses finalized snapshot (not live 3D preview).
 */
function preferFinalizedDesignMetrics(
    embeddedInPricingModal: boolean,
    design: SelectedDesignForPricing | null | undefined,
): boolean {
    if (isAiLayoutPricingDesign(design) || isTreeCutLayoutPricingDesign(design)) return false;
    if (!embeddedInPricingModal) return true;
    return !design || isFinalizedProductsSolarPricingDesign(design);
}

function resolveDesignForPricingPayload(
    embeddedInPricingModal: boolean,
    designForTrigger: SelectedDesignForPricing | null | undefined,
    selectedDesignForPricing: SelectedDesignForPricing | null | undefined,
): SelectedDesignForPricing | null {
    if (embeddedInPricingModal) {
        return designForTrigger ?? selectedDesignForPricing ?? null;
    }
    const sel = selectedDesignForPricing;
    if (
        sel &&
        !isAiLayoutPricingDesign(sel) &&
        !isTreeCutLayoutPricingDesign(sel) &&
        !isFinalizedProductsSolarPricingDesign(sel)
    ) {
        return sel;
    }
    return null;
}

/**
 * Products path: live viewer (finalized oneDRAW sim) first, then RetrieveSystemDesign, then selection / utility.
 * AI layout $: explicit selection first, then viewer, snapshot, utility.
 * Finalized inline grid: design snapshot first (matches System & Design), then viewer fallback.
 */
function resolvedSystemSizeKwForPayload(
    design: SelectedDesignForPricing | null | undefined,
    base: Record<string, unknown>,
    systemDesignSnapshot: Record<string, unknown> | null | undefined,
    viewerTotalSystemKw: number | undefined,
    preferDesignSnapshot = false,
): number {
    const fromSel = positiveKwFromUnknown(design?.systemSizeKw);
    const dd = systemDesignSnapshot ? parseDesignDataRecord(systemDesignSnapshot) : null;
    const fromSnap =
        systemDesignSnapshot != null ? systemSizeKwFromSnapshot(systemDesignSnapshot, dd) : null;
    const fromViewer = viewerLiveSystemKw(viewerTotalSystemKw);

    if (isTreeCutLayoutPricingDesign(design)) {
        if (fromSel != null) return fromSel;
        if (fromSnap != null && fromSnap > 0) return fromSnap;
        if (fromViewer != null) return fromViewer;
        return positiveKwFromUnknown(base.SystemSize) ?? 0;
    }

    if (isAiLayoutPricingDesign(design)) {
        if (fromSel != null) return fromSel;
        if (fromViewer != null) return fromViewer;
        if (fromSnap != null && fromSnap > 0) return fromSnap;
    } else if (preferDesignSnapshot) {
        if (fromSnap != null && fromSnap > 0) return fromSnap;
        if (fromViewer != null) return fromViewer;
        if (fromSel != null) return fromSel;
    } else {
        if (fromViewer != null) return fromViewer;
        if (fromSnap != null && fromSnap > 0) return fromSnap;
        if (fromSel != null) return fromSel;
    }
    return positiveKwFromUnknown(base.SystemSize) ?? 0;
}

/** Production (kWh/yr) from Design & Product Details or layout $ selection — preferred for pricing API over utility usage. */
function designAnnualProductionKwhFromSelection(
    design: SelectedDesignForPricing | null | undefined,
): number | null {
    if (!design) return null;
    const est = design.estimatedAnnualProductionKwh;
    if (est != null && Number.isFinite(est) && est > 0) return Math.round(est);
    const pw = design.productionW;
    if (pw != null && Number.isFinite(pw) && pw > 0) return Math.round(Number(pw));
    return null;
}

/**
 * Products: design snapshot first when preferDesignSnapshot (finalized inline grid), else live viewer first.
 * AI layout $: selection first, then viewer, snapshot, utility.
 */
function resolvedAnnualProductionKwhForPayload(
    design: SelectedDesignForPricing | null | undefined,
    base: Record<string, unknown>,
    sfAnnualUsage: number | null | undefined,
    systemDesignSnapshot: Record<string, unknown> | null | undefined,
    viewerActualAnnualKwh: number | undefined,
    preferDesignSnapshot = false,
    viewerModeCount?: number,
    siteViewerCalculationsPending?: boolean,
): number {
    if (
        preferDesignSnapshot &&
        !isAiLayoutPricingDesign(design) &&
        !isTreeCutLayoutPricingDesign(design) &&
        systemDesignSnapshot
    ) {
        const dd = parseDesignDataRecord(systemDesignSnapshot);
        const systemKw = systemSizeKwFromSnapshot(systemDesignSnapshot, dd);
        const multRaw = systemDesignSnapshot.Multiplier ?? systemDesignSnapshot.multiplier;
        const mult =
            multRaw != null && Number.isFinite(Number(multRaw)) && Number(multRaw) > 0
                ? Number(multRaw)
                : null;
        const fromDesign = resolveSolarAnnualProductionKwh({
            sd: systemDesignSnapshot,
            designData: dd,
            systemKw: systemKw ?? undefined,
            multiplier: mult,
            snapshotOnly: true,
        });
        if (fromDesign != null && fromDesign > 0) return fromDesign;
    }

    const fromSel = designAnnualProductionKwhFromSelection(design);
    const fromSnap = systemDesignSnapshot ? annualProductionKwhFromSnapshot(systemDesignSnapshot) : null;
    const fromViewer = viewerLiveAnnualKwh(viewerActualAnnualKwh);

    let k: number | null = null;
    if (isTreeCutLayoutPricingDesign(design)) {
        if (fromSel != null && fromSel > 0) k = fromSel;
        else if (fromSnap != null && fromSnap > 0) k = fromSnap;
        else if (fromViewer != null) k = fromViewer;
    } else if (isAiLayoutPricingDesign(design)) {
        if (fromSel != null && fromSel > 0) k = fromSel;
        else if (fromViewer != null) k = fromViewer;
        else if (fromSnap != null && fromSnap > 0) k = fromSnap;
    } else if (preferDesignSnapshot) {
        if (fromSnap != null && fromSnap > 0) k = fromSnap;
        else if (fromViewer != null) k = fromViewer;
        else if (fromSel != null && fromSel > 0) k = fromSel;
    } else {
        if (fromViewer != null) k = fromViewer;
        else if (fromSnap != null && fromSnap > 0) k = fromSnap;
        else if (fromSel != null && fromSel > 0) k = fromSel;
    }
    if (k != null && k > 0) return k;
    // const utilityK = resolvedUtilityAnnualKwh(base, sfAnnualUsage);
    // if (utilityK != null) return utilityK;
    return Number(design?.estimatedAnnualProductionKwh) || 0;
}

const getOfferId = (offer: any) => {
    let offerId: string = ''
    if (Array.isArray(offer?.payload)) {
        offerId = offer?.payload[0]?.offerId ?? ''
    }
    return offerId
}

function pickBestGridOffer(offers: any[], partnerNorm: string, isCash: boolean, productType?: string): any | null {
    const valid = (offers ?? []).filter((offer) => offer && !offer.__error);
    if (valid.length === 0) return null;

    const scored = valid
        .map((offer) => {
            const monthly = isCash
                ? (typeof offer.totalCost === 'number' ? offer.totalCost : 0)
                : monthlyFromOffer(offer, partnerNorm);
            const sortKey = Number(monthly) || 0;
            return {
                offer,
                sortKey,
                preferredEscalator: isPreferredEscalatorPct(escalatorFromOffer(offer)),
                apiRecommended: offer.Recommended === true,
            };
        })
        .filter((row) => Number.isFinite(row.sortKey) && row.sortKey > 0);
    if (scored.length === 0) return null;

    // Lightreach Battery Lease has no 2.99% GetPricingLRBO product; Excel 2.99 is leftover solar.
    const isLightreachBattery =
        partnerNorm.includes('lightreach') && String(productType || '').toLowerCase().includes('battery');
    const preferred = isLightreachBattery ? [] : scored.filter((row) => row.preferredEscalator);
    const apiRecommended = scored.filter((row) => row.apiRecommended);
    if (isLightreachBattery) {
        if (apiRecommended.length === 0) return null;
        apiRecommended.sort((a, b) => a.sortKey - b.sortKey);
        return apiRecommended[0].offer;
    }
    const pool = preferred.length ? preferred : apiRecommended.length ? apiRecommended : scored;
    pool.sort((a, b) => a.sortKey - b.sortKey);
    return pool[0].offer;
}

function isBetterGridPriceCell(
    next: PriceCell,
    existing: PriceCell | null,
    partner: string,
): boolean {
    if (!existing) return true;
    const isCash = partner === 'Cash';
    return (
        compareRecommendedSortKeys(
            {
                preferredEscalator: isPreferredEscalatorPct(next.escalator),
                sortKey: next.monthly,
                tier: financeMethodTierFromLabel(next.financeMethod, isCash),
                partner,
            },
            {
                preferredEscalator: isPreferredEscalatorPct(existing.escalator),
                sortKey: existing.monthly,
                tier: financeMethodTierFromLabel(existing.financeMethod, isCash),
                partner,
            },
        ) < 0
    );
}

/** Parse new pricing API: prefer 2.99% escalator, then Recommended === true, per (partner, ProductType). */
function parseNewPricingResponse(apiData: any): { rows: FinancePartnerRow[]; raw: PricingPartnerBlock[] } {
    const raw = (apiData?.data ?? apiData) as PricingPartnerBlock[];
    const partnerMap: Record<string, FinancePartnerRow> = {};

    const initPrices = (): Record<string, PriceCell | null> => {
        const p: Record<string, PriceCell | null> = {};
        FINANCING_PRODUCTS.forEach((col) => { p[col] = null; });
        return p;
    };

    raw.forEach((block) => {
        const partner = normalizePartner(block.FinancePartner || '');
        if (!partnerMap[partner]) partnerMap[partner] = { partner, prices: initPrices() };

        (block.pricing || []).forEach((pricingItem) => {
            const productType = normalizeProductType(pricingItem.ProductType || '');
            if (!productType || !FINANCING_PRODUCTS.includes(productType)) return;

            const partnerNorm = partner.toLowerCase();
            const isCash = partner === 'Cash' || (pricingItem.FinanceMethod || '').toUpperCase() === 'CASH';
            const recommended = pickBestGridOffer(pricingItem.Offer || [], partnerNorm, isCash, productType);
            if (!recommended) return;

            const monthly = isCash
                ? (typeof recommended.totalCost === 'number' ? recommended.totalCost : 0)
                : monthlyFromOffer(recommended, partnerNorm);
            const { term, apr } = termAprNumbersFromOffer(recommended);
            const purchasePrice =
                partner === 'GoodLeap'
                    ? goodLeapPurchasePriceFromOffer(recommended, pricingItem.FinanceMethod || '')
                    : null;
            const nextCell: PriceCell = {
                monthly: Number(monthly) || 0,
                apr,
                term,
                escalator: escalatorFromOffer(recommended),
                recommended: true,
                financeMethod: financeMethodToLabel(pricingItem.financeMethodName || ''),
                payload: partnerNorm.includes('lightreach') && String(productType || '').toLowerCase().includes('battery')
                    ? recommended
                    : recommended.payload,
                ExcelOffer: recommended?.ExcelOffer,
                apiRecommended: recommended.Recommended === true,
                tier: pricingItem.tier,
                ...(purchasePrice != null && purchasePrice > 0 ? { purchasePrice } : {}),
            };
            if (isBetterGridPriceCell(nextCell, partnerMap[partner].prices[productType], partner)) {
                partnerMap[partner].prices[productType] = nextCell;
            }
        });
    });

    const rows = Object.values(partnerMap);
    applySingleRecommendedPerProductColumn(rows);
    return { rows, raw };
}
function QuoteMatchIssueRows({ issue }: { issue: CombinedQuoteMatchIssue }) {
    if (issue.reason === 'missing') {
        return <p className="obd-quote-match__note">Not an active opportunity product.</p>;
    }

    if (issue.reason === 'partner') {
        return (
            <dl className="obd-quote-match__pairs">
                <div className="obd-quote-match__pair">
                    <dt>Saved partner</dt>
                    <dd>{issue.savedPartner?.trim() || 'None'}</dd>
                </div>
                <div className="obd-quote-match__pair">
                    <dt>This plan</dt>
                    <dd>{issue.planPartner?.trim() || '—'}</dd>
                </div>
            </dl>
        );
    }

    return (
        <dl className="obd-quote-match__pairs">
            <div className="obd-quote-match__pair">
                <dt>Saved method</dt>
                <dd>{issue.savedMethod?.trim() || 'None'}</dd>
            </div>
            <div className="obd-quote-match__pair">
                <dt>This plan</dt>
                <dd>{issue.planMethod?.trim() || '—'}</dd>
            </div>
        </dl>
    );
}

export interface FinancingGridCardProps {
    /** When true, the section is considered open (e.g. dashboard visible). */
    dashboardOpen?: boolean;
    /** When set (e.g. timestamp), re-run pricing load (e.g. $ / open grid). Uses cache when the payload key matches. */
    fetchTrigger?: number | null;
    /** When provided (e.g. from $ click), use this design for the payload so production/systemSize are correct. Overrides store. */
    designForTrigger?: SelectedDesignForPricing | null;
    /** Called when user clicks the refresh button to reload pricing. */
    priceQuote?: any;
    onRefreshRequested?: () => void;
    /** Rendered inside the pricing grid pop-out modal: no accordion, tighter chrome, summary + actions on one row when space allows. */
    embeddedInPricingModal?: boolean;
    systemDesignMissingItems?: SystemDesignMissingItem[];
}


export const FinancingGridCard: React.FC<FinancingGridCardProps> = ({
    dashboardOpen = true,
    fetchTrigger = null,
    designForTrigger = null,
    priceQuote = null,
    onRefreshRequested,
    embeddedInPricingModal = false,
    systemDesignMissingItems: systemDesignMissingItemsProp,
}) => {
    const {
        financingGrid,
        setFinancingGrid,
        selectedFinanceCells,
        toggleFinanceCell,
        addFinanceCell,
        setPricingSelectionLoading,
        setQuotesRefreshTrigger,
        setCommerceHubProductsOpen,
        setCommerceHubQuotesOpen,
    } = useOneButtonStore();
    const networkRefreshTrigger = useOneButtonStore((s) => s.pricingNetworkRefreshTrigger);
    const pricingProductsEnabled = useOneButtonStore((s) => s.pricingProductsEnabled);
    const hoverPendingGenerateQuote = useOneButtonStore((s) => s.hoverPendingGenerateQuote);
    const setHoverPendingGenerateQuote = useOneButtonStore((s) => s.setHoverPendingGenerateQuote);
    const [sectionOpen, setSectionOpen] = useSunnyFocusedSectionOpen('financing', {
        enabled: !embeddedInPricingModal,
        fallbackOpen: true,
    });
    const [loading, setLoading] = useState(false);
    const pricingRequestsInFlight = useOneButtonStore((s) => s.pricingRequestsInFlight);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [financingView, setFinancingView] = useState<'ai-plans' | 'combined' | 'grid'>('ai-plans');
    const [viewAllOpen, setViewAllOpen] = useState(false);
    const [allPricingRaw, setAllPricingRaw] = useState<PricingPartnerBlock[] | null>(null);
    const [confirmQuoteCellKey, setConfirmQuoteCellKey] = useState<selectedQuote | null>(null);
    const [quoteMatchPopup, setQuoteMatchPopup] = useState<{
        title: string;
        intro: string;
        issues: CombinedQuoteMatchIssue[];
    } | null>(null);
    const [quoteForm, setQuoteForm] = useState<QuoteFormState | null>(null);
    const [solarQuoteForm, setSolarQuoteForm] = useState<SolarQuoteFormState | null>(null);
    const [pricingFallbackFlags, setPricingFallbackFlags] = useState<{
        roof: string | null;
        battery: string | null;
    }>({ roof: null, battery: null });
    const theme = useOneButtonStore((s) => s.theme);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const utilityCompanyName = useOneButtonStore((s) => s.utilityCompanyName);
    const selectedDesignForPricing = useOneButtonStore((s) => s.selectedDesignForPricing);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const priceQuoteDetails = useOneButtonStore((s) => s.priceQuoteDetails);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const SFannualUsage = useViewerStore((s) => s.SFannualUsage);
    const actualWattage = useViewerStore((s) => s.actualWattage);
    const totalSystemSize = useViewerStore((s) => s.totalSystemSize);
    const viewerModeCount = useViewerStore((s) => s.modeCount);
    const siteViewerCalculationsPending = useViewerStore((s) => s.siteViewerCalculationsPending);
    const roofSquareTableData = useViewerStore((s) => s.roofSquareTableData);
    const designList = useViewerStore((s) => s.designList);
    const layoutMetricsByKey = useViewerStore((s: any) => s.layoutMetricsByKey);
    const liveMultiplier = useViewerStore((s) => s.multiplier);
    const liveModuleMultiplierAverage = useViewerStore((s) => s.moduleMultiplierAverage);
    const numericOpportunityId = useOneButtonStore((s) => s.opportunityID);
    const pricingCacheRef = useRef<Record<string, any>>({});
    const pricingInFlightRef = useRef<Record<string, Promise<unknown>>>({});
    const lastUserFetchTriggerRef = useRef<number | null>(null);
    const lastConsumedUtilityCodeRef = useRef<string | null>(null);
    const siteIdRef = useRef<string>('');
    const lastConsumedNetworkRefreshRef = useRef<number | null>(null);

    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const generateQuoteAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Generate_Quote_Access');
    const isGenerateQuoteDisabled = generateQuoteAccess !== 'Edit' && generateQuoteAccess !== 'View';
    const systemDesignMissingItemsHook = useSystemDesignMissingItems();
    const systemDesignMissingItems = systemDesignMissingItemsProp ?? systemDesignMissingItemsHook;
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const systemDesignLosses = useOneButtonStore((s) => s.systemDesignLosses);
    const financePartnerSyncStatus = useOneButtonStore((s) => s.financePartnerSyncStatus);
    const financePartnerRawSyncStatuses = useOneButtonStore((s) => s.financePartnerRawSyncStatuses);
    const [noticeApi, noticeContextHolder] = notification.useNotification({ maxCount: 3 });
    const notifClass = `obd-notification obd-theme-${theme}`;

    const syncGateBase = useMemo(
        () => ({
            opportunityDetails,
            financePartnerSyncStatus,
            financePartnerRawSyncStatuses,
        }),
        [opportunityDetails, financePartnerSyncStatus, financePartnerRawSyncStatuses],
    );

    const isQuoteSyncBlockedForScope = useCallback(
        (scope?: GenerateQuoteSyncScope) =>
            evaluateGenerateQuoteSyncGate({ ...syncGateBase, scope }).blocked,
        [syncGateBase],
    );

    const scopeFromSyncTarget = useCallback((target?: QuoteSyncTarget): GenerateQuoteSyncScope | undefined => {
        if (!target) return undefined;
        return {
            quotePartner: target.gridPartner,
            quotePartners: target.quotePartners,
            quoteProductTypes: target.productTypes ?? [target.productTypeName],
        };
    }, []);

    const quoteSyncBlockedHintForPartnerProduct = useCallback(
        (partner: string, productTypes: Array<'Solar' | 'Roof' | 'Battery'>) =>
            isQuoteSyncBlockedForScope({ quotePartner: partner, quoteProductTypes: productTypes })
                ? 'Click to open Partner Sync.'
                : undefined,
        [isQuoteSyncBlockedForScope],
    );

    const quoteSyncBlockedHintForScope = useCallback(
        (scope?: GenerateQuoteSyncScope) =>
            scope && isQuoteSyncBlockedForScope(scope) ? 'Click to open Partner Sync.' : undefined,
        [isQuoteSyncBlockedForScope],
    );

    const isGenerateQuotePermissionDisabled = isGenerateQuoteDisabled || embeddedInPricingModal;

    const showQuoteDesignBlockedModal = useCallback(
        (productTypes: readonly QuoteProductType[]) => {
            const reason =
                quoteBlockedReasonFromItems(productTypes, systemDesignMissingItems) ??
                'Complete required design details before generating a quote.';
            Modal.warning({
                title: 'Design required for quote',
                content: reason,
                okText: 'OK',
                centered: true,
                zIndex: 14000,
                className: `obd-quote-design-blocked-modal obd-theme-${theme}`,
                rootClassName: `obd-theme-${theme}`,
            });
        },
        [systemDesignMissingItems, theme],
    );

    const notifyQuoteDesignBlocked = useCallback(
        (reason: string) => {
            Modal.warning({
                title: 'Design required for quote',
                content: reason,
                okText: 'OK',
                centered: true,
                zIndex: 14000,
                className: `obd-quote-design-blocked-modal obd-theme-${theme}`,
                rootClassName: `obd-theme-${theme}`,
            });
        },
        [theme],
    );

    const quoteDesignBlockedReasonForProducts = useCallback(
        (productTypes: readonly QuoteProductType[]) =>
            quoteBlockedReasonFromItems(productTypes, systemDesignMissingItems) ?? undefined,
        [systemDesignMissingItems],
    );

    /**
     * A combined column (Solar + Battery, Battery + Roof, …) can only be quoted when every
     * product it covers sits on the row's finance partner. A cross-partner combo — e.g. Battery
     * on LightReach while Solar is on IGS — is unquotable, so it must be rejected outright
     * rather than handed to the Partner Sync gate, which would offer a sync that cannot help.
     */
    const combinedColumnPartnerMismatch = useCallback(
        (rowPartner: string, col: FinancingProduct | string): boolean => {
            const types = atomicProductTypesFromFinancingProduct(col);
            if (types.length < 2) return false;
            const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
            const active = (Array.isArray(raw) ? raw : []).filter(
                (p: any) => p.ActiveFlag && types.includes(p.Product_Type_Name),
            );
            // Missing products are reported by the existing "product not selected" flows.
            if (active.length < types.length) return false;
            return active.some((p: any) => {
                const partner = String(p.Finance_Partner_Display_Name ?? '').trim();
                // An unset partner is still being configured; leave it to the existing flows.
                return partner !== '' && !financePartnerKeysEqual(partner, rowPartner);
            });
        },
        [opportunityDetails],
    );

    const financeHubCtx = useOptionalProductsFinanceHubContext();

    const openPartnerSyncForQuoteBlock = useCallback(
        (syncTarget?: QuoteSyncTarget) => {
            const scope = scopeFromSyncTarget(syncTarget ?? defaultQuoteSyncTarget(opportunityDetails));
            const incomplete = findFirstIncompleteQuoteSyncTarget(syncGateBase, scope);
            const target: QuoteSyncTarget | undefined = incomplete
                ? {
                      /**
                       * Keep the clicked column's primary product ("Solar + Battery" → Solar);
                       * `incomplete` only tells us which partner still needs syncing, and its
                       * product type is whichever atomic type tripped the gate first.
                       */
                      productTypeName: syncTarget?.productTypeName ?? incomplete.productTypeName,
                      gridPartner: incomplete.partner,
                      productTypes: scope?.quoteProductTypes,
                      quotePartners: scope?.quotePartners,
                  }
                : syncTarget ?? defaultQuoteSyncTarget(opportunityDetails);
            if (!target) {
                showPartnerSyncRequiredModal(theme);
                return;
            }
            const opened = openPartnerSyncForQuoteTarget(target, opportunityDetails, financeHubCtx);
            if (!opened) {
                showPartnerSyncRequiredModal(
                    theme,
                    'Complete Partner Sync on the system design card for this finance partner before generating a quote.',
                );
            }
        },
        [opportunityDetails, financeHubCtx, scopeFromSyncTarget, syncGateBase, theme],
    );

    const assertCanGenerateQuote = useCallback(
        (syncTarget?: QuoteSyncTarget): boolean => {
            if (isGenerateQuotePermissionDisabled) return false;
            const productTypes =
                syncTarget?.productTypes ??
                (syncTarget?.productTypeName
                    ? atomicProductTypesFromFinancingProduct(syncTarget.productTypeName)
                    : (['Solar'] as QuoteProductType[]));
            if (hasQuoteHardMissForProducts(productTypes, systemDesignMissingItems)) {
                showQuoteDesignBlockedModal(productTypes);
                return false;
            }
            if (isQuoteSyncBlockedForScope(scopeFromSyncTarget(syncTarget))) {
                openPartnerSyncForQuoteBlock(syncTarget);
                return false;
            }
            return true;
        },
        [
            isGenerateQuotePermissionDisabled,
            systemDesignMissingItems,
            showQuoteDesignBlockedModal,
            isQuoteSyncBlockedForScope,
            scopeFromSyncTarget,
            openPartnerSyncForQuoteBlock,
        ],
    );

    const assertDesignLossesCompatibleForQuote = useCallback(
        (quoteProduct: string | undefined): boolean => {
            const mismatch = evaluateDesignLossesMismatchForQuote(
                systemDesignLosses,
                opportunityDetails,
                quoteProduct,
                financeHubCtx?.rows,
            );
            if (!mismatch.blocked) return true;
            showDesignLossesMismatchModal(
                mismatch.message ??
                    'Design losses do not match the selected finance partner.',
                theme,
            );
            return false;
        },
        [systemDesignLosses, opportunityDetails, financeHubCtx?.rows, theme],
    );

    /** Hover Convert → open Generate Quote with Roof selection (consume-once). */
    useEffect(() => {
        if (!hoverPendingGenerateQuote) return;
        if (embeddedInPricingModal) return;
        if (isGenerateQuoteDisabled) return;
        const pending = useOneButtonStore.getState().hoverPendingGenerateQuote;
        if (!pending) return;
        const pendingProductTypes = atomicProductTypesFromFinancingProduct(pending.product);
        if (hasQuoteHardMissForProducts(pendingProductTypes, systemDesignMissingItems)) return;
        useOneButtonStore.getState().setHoverPendingGenerateQuote(null);
        setConfirmQuoteCellKey({
            product: pending.product,
            partner: pending.partner,
            methord: pending.methord ?? undefined,
            price: pending.price ?? null,
            ExcelOffer: pending.ExcelOffer ?? null,
            payload: pending.payload ?? null,
            products: pending.products as selectedQuote[] | undefined,
        });
        setCommerceHubQuotesOpen(true);
    }, [
        hoverPendingGenerateQuote,
        embeddedInPricingModal,
        isGenerateQuoteDisabled,
        systemDesignMissingItems,
        setCommerceHubQuotesOpen,
    ]);

    const currentSiteId = useMemo(() => {
        const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        return (
            qs?.get('sfid') ||
            (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) ||
            'unknown-site'
        );
    }, []);

    const showTreeCutCostColumn = useMemo(() => {
        // Only show TREE column when pricing was opened from a tree-cutting scenario ($ click).
        return embeddedInPricingModal && isTreeCutLayoutPricingDesign(designForTrigger);
    }, [embeddedInPricingModal, designForTrigger]);

    const designForPricingPayload = useMemo(
        () => resolveDesignForPricingPayload(embeddedInPricingModal, designForTrigger, selectedDesignForPricing),
        [embeddedInPricingModal, designForTrigger, selectedDesignForPricing],
    );

    /** Inline Financing Options for the finalized design — not modal AI/tree scenario context. */
    const isFinalizedInlineFinancing = !embeddedInPricingModal && !designForPricingPayload;

    const useFinalizedDesignMetrics = preferFinalizedDesignMetrics(
        embeddedInPricingModal,
        designForPricingPayload,
    );

    /** Finalized grid: only refetch when RetrieveSystemDesign metrics change — not live 3D preview. */
    const finalizedDesignMetricsKey = useMemo(() => {
        if (!useFinalizedDesignMetrics || !systemDesign) return '';
        const snap = systemDesign as Record<string, unknown>;
        const dd = parseDesignDataRecord(snap);
        const kw = systemSizeKwFromSnapshot(snap, dd);
        const prod = annualProductionKwhFromSnapshot(snap);
        const mult = snap.Multiplier ?? snap.multiplier;
        return `${kw ?? ''}|${prod ?? ''}|${mult ?? ''}`;
    }, [useFinalizedDesignMetrics, systemDesign]);

    const pricingMetricsDep = useMemo(() => {
        if (useFinalizedDesignMetrics) return `finalized:${finalizedDesignMetricsKey}`;
        return `live:${totalSystemSize ?? ''}|${actualWattage ?? ''}`;
    }, [useFinalizedDesignMetrics, finalizedDesignMetricsKey, totalSystemSize, actualWattage]);

    const treeCutCostForGrid = useMemo((): number | null => {
        if (!showTreeCutCostColumn) return null;
        const d = designForPricingPayload;
        const raw = (d as any)?.TreeCutCost ?? (d as any)?.treeCutCost ?? (d as any)?.cutCost ?? null;
        const n = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
        return n != null && n >= 0 ? n : null;
    }, [showTreeCutCostColumn, designForPricingPayload]);

    useEffect(() => {
        if (siteIdRef.current && siteIdRef.current !== currentSiteId) {
            pricingCacheRef.current = {};
            clearPricingSessionCache();
        }
        siteIdRef.current = currentSiteId;
    }, [currentSiteId]);

    useEffect(() => {
        const userTriggeredFetch = fetchTrigger != null && fetchTrigger > 0;
        const shouldFetch = dashboardOpen || userTriggeredFetch;
        if (!shouldFetch) return;
        const refreshBypassNetwork =
            networkRefreshTrigger != null &&
            networkRefreshTrigger > 0 &&
            networkRefreshTrigger !== lastConsumedNetworkRefreshRef.current;
        const base = (solarUtilityData?.SolarUtility ?? {}) as Record<string, unknown>;
        const currentUtilityCode = resolveUtilityCompanyCode(base, utilityCompanyName);
        const utilityInputsChanged =
            lastConsumedUtilityCodeRef.current != null &&
            lastConsumedUtilityCodeRef.current !== currentUtilityCode;
        /* $ / modal: one request per fetchTrigger bump (design may update in the prior render). */
        if (!dashboardOpen && userTriggeredFetch && !refreshBypassNetwork && !utilityInputsChanged) {
            if (lastUserFetchTriggerRef.current === fetchTrigger) return;
            lastUserFetchTriggerRef.current = fetchTrigger;
        }
        /* When user clicks $ or toggles a product price on, fetch even if solarUtilityData isn't loaded yet. */
        if (!solarUtilityData && !userTriggeredFetch && !refreshBypassNetwork) return;
        setLoadError(null);
        setLoading(true);
        setPricingSelectionLoading(true);
        const designToUse = designForPricingPayload;
        const preferDesignSnapshot = useFinalizedDesignMetrics;
        const viewerKwForPayload = preferDesignSnapshot ? undefined : totalSystemSize;
        const viewerProdForPayload = preferDesignSnapshot ? undefined : actualWattage;
        const sdSnap = systemDesign ? (systemDesign as Record<string, unknown>) : null;
        const systemSizeNum = resolvedSystemSizeKwForPayload(
            designToUse,
            base,
            sdSnap,
            viewerKwForPayload,
            preferDesignSnapshot,
        );
        const estimatedAnnualProductionValue = resolvedAnnualProductionKwhForPayload(
            designToUse,
            base,
            SFannualUsage,
            sdSnap,
            viewerProdForPayload,
            preferDesignSnapshot,
            viewerModeCount,
            siteViewerCalculationsPending,
        );
        const estProdNum = Number(estimatedAnnualProductionValue);
        const allPricingProducts = pricingProductTypeNames(opportunityDetails);
        const linesToShow = embeddedInPricingModal
            ? allPricingProducts
            : filterPricingProductsForApi(allPricingProducts, pricingProductsEnabled);
        if (!embeddedInPricingModal && linesToShow.length === 0) {
            setFinancingGrid([]);
            setAllPricingRaw(null);
            setLoadError(null);
            setLoading(false);
            setPricingSelectionLoading(false);
            useOneButtonStore.setState({ pricingDesignFingerprint: null });
            return;
        }
        if (embeddedInPricingModal) {
            const requiresSolarMetrics = pricingPayloadRequiresSolarMetrics({ products: linesToShow });
            if (requiresSolarMetrics) {
                if (!Number.isFinite(estProdNum) || estProdNum <= 0) {
                    const msg = 'Estimated annual production must be greater than 0 to price this design.';
                    setLoadError(msg);
                    setLoading(false);
                    setPricingSelectionLoading(false);
                    return;
                }
                if (!Number.isFinite(systemSizeNum) || systemSizeNum <= 0) {
                    const msg = 'System size must be greater than 0 to price this design.';
                    setLoadError(msg);
                    setLoading(false);
                    setPricingSelectionLoading(false);
                    return;
                }
            }
        }
        let cancelled = false;
        void (async () => {
        const needsRoof = opportunityNeedsRoofSquares(linesToShow);
        const needsBattery = opportunityNeedsBatteryDesign(linesToShow);
        const designArrayModules =
            countDesignArrayModules(systemDesign) ??
            (typeof viewerModeCount === 'number' && viewerModeCount > 0 ? viewerModeCount : null);
        const roof = await resolveRoofSquaresWithFetch({
            systemDesign,
            priceQuoteOverrides,
            priceQuoteDetails,
            oneDrawTable: sumOneDrawRoofSquareTable(roofSquareTableData),
            oneDrawDesignList: sumOneDrawDesignListSquares(designList),
            designArrayModules,
            includeRoofProduct: needsRoof,
            salesforceId: String(systemDesign?.SalesForce_Id ?? opportunityDetails?.SalesForce_Id ?? ''),
            designSource: String(
                (systemDesign as { Design_Source?: string } | null)?.Design_Source ??
                    opportunityDetails?.Design_Source ??
                    '',
            ),
            applyRoofWastage: (systemDesign as { ApplyRoofWastage?: boolean } | null)?.ApplyRoofWastage === true,
        });
        if (cancelled) return;
        const battery = resolveBatteryDesignForPricing(systemDesign, { includeBatteryProduct: needsBattery });
        const fallbackValidation = validatePricingProductFallbacks(linesToShow, roof, battery);
        if (fallbackValidation) {
            setLoadError(fallbackValidation);
            setLoading(false);
            setPricingSelectionLoading(false);
            return;
        }
        setPricingFallbackFlags({
            roof: needsRoof ? roofSquaresSourceLabel(roof.source) : null,
            battery: needsBattery ? batteryDesignSourceLabel(battery.source) : null,
        });
        const roofPitch =
            roof.standard > 0 && roof.low === 0
                ? 'Normal Slope'
                : roof.low > 0
                  ? 'Low Slope'
                  : 'Normal Slope';
        const lowRoof = Math.round(roof.low);
        const standardRoof = Math.round(roof.standard);
        const batteryCount = battery.quantity;
        const treeCutCostForPayloadRaw =
            isTreeCutLayoutPricingDesign(designToUse) || (embeddedInPricingModal && isTreeCutLayoutPricingDesign(designForTrigger ?? null))
                ? (designToUse as any)?.TreeCutCost ?? null : null;
        const treeCutCostForPayload =
            treeCutCostForPayloadRaw != null && Number.isFinite(Number(treeCutCostForPayloadRaw))
                ? Number(treeCutCostForPayloadRaw)
                : null;
        const payloadBase = {
            state: String(base.StateId ?? ''),
            systemSize: systemSizeNum,
            estimatedAnnualProduction: Math.round(estProdNum),
            utilityCompanyCode: currentUtilityCode,
            batteryCount,
            ...(needsBattery
                ? {
                      batteryManufacturer: battery.manufacturer,
                      batteryModelName: battery.name,
                  }
                : {}),
            roofPitch,
            lowRoof,
            standardRoof,
            opportunityId: numericOpportunityId,
            ...(treeCutCostForPayload != null ? { TreeCutCost: treeCutCostForPayload } : {}),
        };
        const designFingerprint = buildPricingDesignFingerprint(payloadBase);
        useOneButtonStore.setState({ pricingDesignFingerprint: designFingerprint });

        const forceSessionRefetch = refreshBypassNetwork || utilityInputsChanged;
        let linesToFetch = linesToShow;
        let sessionCachedBody: unknown | null = null;
        if (!embeddedInPricingModal) {
            const incremental = computeIncrementalPricingFetch(
                allPricingProducts,
                pricingProductsEnabled,
                designFingerprint,
                forceSessionRefetch,
            );
            linesToFetch = incremental.linesToFetch;
            sessionCachedBody = incremental.cachedBody;

            if (linesToFetch.length === 0 && sessionCachedBody) {
                applyPricingBodyFromCache(sessionCachedBody);
                setLoading(false);
                setPricingSelectionLoading(false);
                return;
            }
            if (linesToFetch.length === 0) {
                setLoading(false);
                setPricingSelectionLoading(false);
                return;
            }
        }

        const requiresSolarMetrics = pricingPayloadRequiresSolarMetrics({ products: linesToFetch });
        if (requiresSolarMetrics) {
            if (!Number.isFinite(estProdNum) || estProdNum <= 0) {
                const msg = 'Estimated annual production must be greater than 0 to price this design.';
                setLoadError(msg);
                setLoading(false);
                setPricingSelectionLoading(false);
                return;
            }
            if (!Number.isFinite(systemSizeNum) || systemSizeNum <= 0) {
                const msg = 'System size must be greater than 0 to price this design.';
                setLoadError(msg);
                setLoading(false);
                setPricingSelectionLoading(false);
                return;
            }
        }

        const pricingPayload = {
            ...payloadBase,
            products: linesToFetch,
        };

        const pricingCacheKey = `${currentSiteId}::${JSON.stringify(pricingPayload)}`;
        const cacheStorageKey = `obd-pricing-cache:${pricingCacheKey}`;
        const usePricingCache = !refreshBypassNetwork && !utilityInputsChanged;
        lastConsumedUtilityCodeRef.current = currentUtilityCode;

        function applyPricingBodyFromCache(body: any) {
            const { rows, raw: blocksRaw } = parseNewPricingResponse(body);
            const blocks =
                treeCutCostForPayload != null
                    ? (blocksRaw || []).map((b: any) => ({
                          ...b,
                          pricing: (b?.pricing || []).map((p: any) => ({
                              ...p,
                              Offer: (p?.Offer || []).map((o: any) => ({
                                  ...o,
                                  TreeCutCost: o?.TreeCutCost ?? treeCutCostForPayload,
                              })),
                          })),
                      }))
                    : blocksRaw;
            setFinancingGrid(rows);
            setAllPricingRaw(blocks);
        }

        const applyPricingBody = (body: any) => {
            applyPricingBodyFromCache(body);
        };

        if (!embeddedInPricingModal && sessionCachedBody && linesToFetch.length > 0) {
            applyPricingBodyFromCache(sessionCachedBody);
        }

        const inMemoryCached = usePricingCache ? pricingCacheRef.current[pricingCacheKey] : undefined;
        if (inMemoryCached) {
            if (!embeddedInPricingModal) {
                upsertPricingSessionCache(designFingerprint, linesToFetch, inMemoryCached);
                const mergedBody =
                    getPricingSessionCacheForFingerprint(designFingerprint)?.body ?? inMemoryCached;
                applyPricingBody(mergedBody);
            } else {
                applyPricingBody(inMemoryCached);
            }
            setLoading(false);
            setPricingSelectionLoading(false);
            return;
        }

        if (usePricingCache && typeof window !== 'undefined') {
            try {
                const serialized = sessionStorage.getItem(cacheStorageKey);
                if (serialized) {
                    const cachedBody = JSON.parse(serialized);
                    pricingCacheRef.current[pricingCacheKey] = cachedBody;
                    if (!embeddedInPricingModal) {
                        upsertPricingSessionCache(designFingerprint, linesToFetch, cachedBody);
                        const mergedBody =
                            getPricingSessionCacheForFingerprint(designFingerprint)?.body ?? cachedBody;
                        applyPricingBody(mergedBody);
                    } else {
                        applyPricingBody(cachedBody);
                    }
                    setLoading(false);
                    setPricingSelectionLoading(false);
                    return;
                }
            } catch {
                // Ignore cache parse/storage errors and fallback to network.
            }
        }

        if (refreshBypassNetwork) {
            lastConsumedNetworkRefreshRef.current = networkRefreshTrigger;
        }

        const inflight = pricingInFlightRef.current[pricingCacheKey];
        if (inflight) {
            void inflight.then((body) => {
                if (!body) return;
                if (!embeddedInPricingModal) {
                    upsertPricingSessionCache(designFingerprint, linesToFetch, body);
                    const mergedBody =
                        getPricingSessionCacheForFingerprint(designFingerprint)?.body ?? body;
                    applyPricingBody(mergedBody);
                } else {
                    applyPricingBody(body);
                }
                setLoading(false);
                setPricingSelectionLoading(false);
            });
            return;
        }

        const pricingRequest = quoteServices
            .getPricing(pricingPayload)
            .then((response) => {
                const body = response?.data;
                pricingCacheRef.current[pricingCacheKey] = body;
                if (typeof window !== 'undefined') {
                    try {
                        sessionStorage.setItem(cacheStorageKey, JSON.stringify(body));
                    } catch {
                        // Ignore quota/storage issues.
                    }
                }
                if (!embeddedInPricingModal) {
                    if (forceSessionRefetch) {
                        replacePricingSessionCache(designFingerprint, linesToFetch, body);
                    } else {
                        upsertPricingSessionCache(designFingerprint, linesToFetch, body);
                    }
                    const mergedBody =
                        getPricingSessionCacheForFingerprint(designFingerprint)?.body ?? body;
                    applyPricingBody(mergedBody);
                } else {
                    applyPricingBody(body);
                }
                return body;
            })
            .catch((err) => {
                if (quoteServices.isPricingRequestAborted(err)) {
                    return;
                }
                setFinancingGrid([]);
                setAllPricingRaw(null);
                const message = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Failed to load pricing';
                setLoadError(String(message));
                throw err;
            })
            .finally(() => {
                delete pricingInFlightRef.current[pricingCacheKey];
                setLoading(false);
                setPricingSelectionLoading(false);
            });

        pricingInFlightRef.current[pricingCacheKey] = pricingRequest;
        })();
        return () => {
            cancelled = true;
        };
    }, [
        dashboardOpen,
        fetchTrigger,
        networkRefreshTrigger,
        embeddedInPricingModal,
        designForPricingPayload,
        solarUtilityData,
        utilityCompanyName,
        systemDesign,
        setPricingSelectionLoading,
        SFannualUsage,
        pricingMetricsDep,
        viewerModeCount,
        siteViewerCalculationsPending,
        isFinalizedInlineFinancing,
        useFinalizedDesignMetrics,
        currentSiteId,
        numericOpportunityId,
        opportunityDetails,
        priceQuoteOverrides,
        priceQuoteDetails,
        roofSquareTableData,
        designList,
        pricingProductsEnabled,
    ]);

    /** Always show all partners and product columns; merge API rows with default partner list, fill missing with null → N/A */
    const displayGridRows = useMemo((): FinancePartnerRow[] => {
        const initPrices = (): Record<string, PriceCell | null> => {
            const p: Record<string, PriceCell | null> = {};
            FINANCING_PRODUCTS.forEach((col) => { p[col] = null; });
            return p;
        };
        const defaultRows = DEFAULT_FINANCE_PARTNERS.map((partner) => {
            const fromApi = financingGrid.find((r) => r.partner === partner);
            return fromApi ?? { partner, prices: initPrices() };
        });
        const apiOnlyPartners = financingGrid.filter((r) => !DEFAULT_FINANCE_PARTNERS.includes(r.partner));
        const merged = [...defaultRows, ...apiOnlyPartners];
        if (embeddedInPricingModal) return merged;
        return filterFinanceGridByEnabledProducts(merged, pricingProductsEnabled);
    }, [financingGrid, embeddedInPricingModal, pricingProductsEnabled]);

    const combinedFinancingSummary = useMemo(
        () => buildRecommendedCombinedFinancing(displayGridRows),
        [displayGridRows],
    );

    /**
     * A Pricing call started elsewhere (design card EPC, tree-cut scenario) still decides what
     * this section can show, so surface it as loading instead of the "click $" empty state.
     */
    const pricingBusy = loading || pricingRequestsInFlight > 0;

    const combinedMonthlyDisplay =
        combinedFinancingSummary.totalMonthly > 0
            ? `$${combinedFinancingSummary.totalMonthly.toFixed(2)}/mo`
            : pricingBusy
              ? '…'
              : '—';

    const utilityBillMonthly = useMemo(
        () => resolveUtilityBillMonthly(solarUtilityData, SFannualUsage),
        [solarUtilityData, SFannualUsage],
    );

    const financingAiPlans = useMemo(
        () =>
            buildFinancingAiPlans({
                rows: displayGridRows,
                combinedSummary: combinedFinancingSummary,
                utilityBillMonthly,
            }),
        [displayGridRows, combinedFinancingSummary, utilityBillMonthly],
    );

    const aiPlansReady = financingAiPlans.length > 0 && !pricingBusy;

    /** Flat rows for "All pricing options" table: partner, product, method, monthly, loan, term, APR, escalator, status */
    const viewAllRows = useMemo((): PricingViewAllRow[] => {
        if (!Array.isArray(allPricingRaw) || allPricingRaw.length === 0) return [];
        const designForHeader = designForPricingPayload;
        const treeDetailsText =
            isTreeCutLayoutPricingDesign(designForHeader) &&
            Array.isArray((designForHeader as any)?.treeDetails) &&
            (designForHeader as any).treeDetails.length > 0
                ? (designForHeader as any).treeDetails
                      .map((t: any) => {
                          const id = String(t?.treeId || '').trim();
                          const dbh = String(t?.dbh || '').trim();
                          const h = String(t?.height || '').trim();
                          const stump = String(t?.stump || '').trim();
                          const bits = [
                              id ? `#${id}` : '',
                              dbh ? `DBH ${dbh}` : '',
                              h ? `H ${h}` : '',
                              stump ? `Stump ${stump}` : '',
                          ].filter(Boolean);
                          return bits.join(' · ');
                      })
                      .filter(Boolean)
                      .join(' | ')
                : undefined;
        const rows: PricingViewAllRow[] = [];
        allPricingRaw.forEach((block) => {
            const partner = normalizePartner(block.FinancePartner || '');
            const partnerNorm = (block.FinancePartner || '').toLowerCase();
            (block.pricing || []).forEach((pricingItem) => {
                const product = normalizeProductType(pricingItem.ProductType || '') ?? pricingItem.ProductType ?? 'Solar';
                const financeMethod = financeMethodToLabel(pricingItem.FinanceMethod || '');
                const offers = pricingItem.Offer || [];
                offers.forEach((offer) => {
                    const err = offer?.__error;
                    const friendlyError = err
                        ? (err.includes('Missing required fields') || err.includes('required')
                            ? 'Pricing unavailable'
                            : 'Pricing unavailable')
                        : null;
                    const recommended = offer.Recommended === true && !err;
                    const monthly = err ? null : monthlyFromOffer(offer, partnerNorm);
                    const loanAmount = loanAmountFromOffer(offer);
                    const { term, apr } = termAprNumbersFromOffer(offer);
                    const escalatorPct = escalatorFromOffer(offer);
                    const treeCutCost = err ? null : treeCutCostFromOffer(offer);
                    const minimumRate = offer?.ExcelOffer?.startingRate ?? null;
                    const rateCap = offer?.ExcelOffer?.rateCap ?? null;
                    rows.push({
                        partner,
                        product,
                        financeMethod,
                        treeDetailsText,
                        treeCutCost,
                        monthly: monthly != null && monthly > 0 ? monthly : null,
                        loanAmount,
                        term: term > 0 ? term : null,
                        apr: apr > 0 ? apr : null,
                        escalatorPct,
                        minimumRate,
                        rateCap,
                        recommended,
                        error: friendlyError,
                    });
                });
            });
        });
        applySingleRecommendedViewAllRows(rows);
        return rows;
    }, [allPricingRaw, designForPricingPayload]);

    const cardMod = embeddedInPricingModal ? ' obd-card--financing--pricing-modal' : '';

    if (loading) {
        return (
            <div className={`obd-card obd-card--financing${cardMod}`}>
                {!embeddedInPricingModal && (
                    <span className="obd-card__label obd-card__label--section-icon">
                        <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                            <CurrencyCircleDollar size={16} weight="duotone" />
                        </span>
                        Financing Options for the Finalized Design
                    </span>
                )}
                <div className="obd-financing__loading">Loading pricing data…</div>
            </div>
        );
    }

    const base = (solarUtilityData?.SolarUtility ?? {}) as Record<string, unknown>;
    const designForHeader = designForPricingPayload;
    const sdHeader = systemDesign ? (systemDesign as Record<string, unknown>) : null;
    const selectedDesignKw = useFinalizedDesignMetrics ? undefined : totalSystemSize;
    const selectedDesignProd = useFinalizedDesignMetrics ? undefined : actualWattage;
    const headerSystemKwNum = resolvedSystemSizeKwForPayload( designForHeader, base, sdHeader, selectedDesignKw, 
        useFinalizedDesignMetrics
    );
    const estimatedKwh = designForHeader?.estimatedAnnualProductionKwh ?? base.AnnualUsage ?? 0;
    const headerUtilityAnnual = resolvedUtilityAnnualKwh(base, SFannualUsage);
    const fromSelH = designAnnualProductionKwhFromSelection(designForHeader);
    const fromSnapH = sdHeader ? annualProductionKwhFromSnapshot(sdHeader) : null;
    const fromViewerH = viewerLiveAnnualKwh(selectedDesignProd);
    let layoutAnnualKwh: number | null = null;
    let headerProductionIsUtility = false;
    let headerProductionFromLiveViewer = false;
    let headerProductionFromTreeScenario = false;
    if (isTreeCutLayoutPricingDesign(designForHeader)) {
        if (fromSelH != null && fromSelH > 0) {
            layoutAnnualKwh = fromSelH;
            headerProductionFromTreeScenario = true;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
        else if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        }
    } else if (isAiLayoutPricingDesign(designForHeader)) {
        if (fromSelH != null && fromSelH > 0) layoutAnnualKwh = fromSelH;
        else if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
    } else if (useFinalizedDesignMetrics && sdHeader) {
        const ddHeader = parseDesignDataRecord(sdHeader);
        const snapKwHeaderResolved = systemSizeKwFromSnapshot(sdHeader, ddHeader);
        const multRawHeader = sdHeader.Multiplier ?? sdHeader.multiplier;
        const multHeader =
            multRawHeader != null && Number.isFinite(Number(multRawHeader)) && Number(multRawHeader) > 0
                ? Number(multRawHeader)
                : null;
        const fromDesignHeader = resolveSolarAnnualProductionKwh({
            sd: sdHeader,
            designData: ddHeader,
            systemKw: snapKwHeaderResolved ?? undefined,
            multiplier: multHeader,
            snapshotOnly: true,
        });
        if (fromDesignHeader != null && fromDesignHeader > 0) {
            layoutAnnualKwh = fromDesignHeader;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
        else if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        } else if (fromSelH != null && fromSelH > 0) layoutAnnualKwh = fromSelH;
    } else {
        if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
        else if (fromSelH != null && fromSelH > 0) layoutAnnualKwh = fromSelH;
    }
    if (layoutAnnualKwh == null || layoutAnnualKwh <= 0) {
        if (headerUtilityAnnual != null) {
            layoutAnnualKwh = headerUtilityAnnual;
            headerProductionIsUtility = true;
        }
    }
    const ddHeader = sdHeader ? parseDesignDataRecord(sdHeader) : null;
    const snapKwHeader = sdHeader ? systemSizeKwFromSnapshot(sdHeader, ddHeader) : null;
    const viewerKwHeader = viewerLiveSystemKw(selectedDesignKw);
    const designKwPositive =
        positiveKwFromUnknown(designForHeader?.systemSizeKw) != null ||
        (snapKwHeader != null && snapKwHeader > 0);
    const utilityOnlySize =
        headerSystemKwNum > 0 &&
        positiveKwFromUnknown(designForHeader?.systemSizeKw) == null &&
        (snapKwHeader == null || snapKwHeader <= 0) &&
        (viewerKwHeader == null || viewerKwHeader <= 0) &&
        positiveKwFromUnknown(base.SystemSize) != null;
    const systemSizeSource = isTreeCutLayoutPricingDesign(designForHeader)
        ? 'Tree-cutting scenario: system size from the scenario row (DC kW) passed when you clicked $.'
        : utilityOnlySize
            ? 'Opportunity utility system size (SolarUtility.SystemSize).'
            : designForHeader?.layoutKey === 'products-solar' || !isAiLayoutPricingDesign(designForHeader)
                ? useFinalizedDesignMetrics
                    ? 'Design & Product Details: system size from RetrieveSystemDesign snapshot (matches System & Design).'
                    : 'Design & Product Details: system size from design API / arrays, live 3D design, or utility when needed.'
                : 'Selected AI layout: module count × module power ÷ 1000.';
    const systemHeader = {
        state: String(base.StateId ?? '—'),
        systemSize: headerSystemKwNum > 0 ? headerSystemKwNum.toFixed(2) : '—',
        systemSizeTitle: systemSizeSource,
        estimatedAnnualProduction:
            layoutAnnualKwh != null
                ? `${layoutAnnualKwh.toLocaleString()} kWh ${headerProductionIsUtility
                    ? '(utility annual)'
                    : headerProductionFromTreeScenario
                        ? '(tree-cutting scenario)'
                        : headerProductionFromLiveViewer
                            ? '(finalized design)'
                            : '(design production)'
                }`
                : headerUtilityAnnual != null
                    ? `${headerUtilityAnnual.toLocaleString()} kWh (utility annual)`
                    : estimatedKwh != null && Number.isFinite(Number(estimatedKwh)) && Number(estimatedKwh) > 0
                        ? `${Number(estimatedKwh).toLocaleString()} kWh`
                        : '—',
        estimatedProductionTitle:
            layoutAnnualKwh != null
                ? headerProductionIsUtility
                    ? 'Utility annual usage (fallback when design production is not on file). Sent to the pricing API.'
                    : headerProductionFromTreeScenario
                        ? 'Annual kWh estimated for this tree-cutting scenario (scaled from site usage or layout production vs baseline DC watts). Sent to the pricing API.'
                        : headerProductionFromLiveViewer
                            ? 'Annual production from the live oneDRAW 3D design (same as System metrics). Sent to the pricing API.'
                            : 'Estimated annual production (kWh) from solar design data or the layout you priced with $. Sent to the pricing API.'
                : headerUtilityAnnual != null
                    ? 'Utility annual usage (used when no design production is available). Sent to the pricing API.'
                    : 'No design production or utility annual usage on file.',
        utilityCompanyCode: resolveUtilityCompanyCode(base, utilityCompanyName) || '—',
    };

    const contentOpen = embeddedInPricingModal || sectionOpen;
    const wrapMod = embeddedInPricingModal ? ' obd-financing__wrap--pricing-modal' : '';
    const productionDisplay = splitFinancingProductionLabel(String(systemHeader.estimatedAnnualProduction || '—'));
    const productionNoteLabel = productionDisplay.note ? formatFinancingProductionNote(productionDisplay.note) : null;
    const systemSizeDisplay =
        systemHeader.systemSize !== '—' ? `${systemHeader.systemSize} kW` : '—';
    const isLiveDesign = !designForHeader || designForHeader.layoutKey === 'products-solar';
    const aiMultiplier = designForHeader?.layoutKey ? layoutMetricsByKey?.[designForHeader.layoutKey]?.multiplier : null;
    const liveRoofWeightedMultiplier =
        typeof liveMultiplier === 'number' && Number.isFinite(liveMultiplier) && liveMultiplier > 0
            ? liveMultiplier
            : null;
    let multiplierDisplay = '—';
    if (isLiveDesign) {
        const liveVal = Number(sdHeader?.Multiplier) || liveRoofWeightedMultiplier || liveModuleMultiplierAverage;
        if (liveVal && liveVal > 0) multiplierDisplay = Number(liveVal).toFixed(2);
    } else {
        // Roof-weighted live multiplier is what finalize saves; card maps store module average.
        const aiVal = liveRoofWeightedMultiplier ?? aiMultiplier ?? designForHeader?.multiplier;
        if (aiVal && aiVal > 0) {
            multiplierDisplay = liveRoofWeightedMultiplier != null
                ? liveRoofWeightedMultiplier.toFixed(2)
                : Number(aiVal).toFixed(4);
        }
    }

    const systemHeaderBlock = (
        <div
            className="obd-financing__system-header obd-financing__system-header--tiles"
            aria-label="Pricing parameters"
        >
            <div className="obd-financing__system-tiles" role="list">
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--state"
                    role="listitem"
                    title={`Project state: ${systemHeader.state}`}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--state" aria-hidden>
                        <MapPin size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">State</span>
                        <span className="obd-financing__system-tile-value">{systemHeader.state}</span>
                    </div>
                </div>
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--system"
                    role="listitem"
                    title={systemHeader.systemSizeTitle}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--system" aria-hidden>
                        <Lightning size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">System</span>
                        <span className="obd-financing__system-tile-value">{systemSizeDisplay}</span>
                    </div>
                </div>
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--utility"
                    role="listitem"
                    title={`Utility: ${systemHeader.utilityCompanyCode}`}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--utility" aria-hidden>
                        <Plug size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">Utility</span>
                        <span className="obd-financing__system-tile-value obd-financing__system-tile-value--clamp">
                            {systemHeader.utilityCompanyCode}
                        </span>
                    </div>
                </div>
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--production"
                    role="listitem"
                    title={systemHeader.estimatedProductionTitle}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--production" aria-hidden>
                        <Sun size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">Est. production</span>
                        <span className="obd-financing__system-tile-value-row">
                            <span className="obd-financing__system-tile-value">{productionDisplay.value}</span>
                            {productionNoteLabel ? (
                                <span className="obd-financing__system-tile-badge">{productionNoteLabel}</span>
                            ) : null}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const toolbarBlock =
        (allPricingRaw && allPricingRaw.length > 0) || onRefreshRequested ? (
            <div
                className="obd-financing__toolbar obd-metrics__action-group"
                role="group"
                aria-label="Financing actions"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="obd-merged-system__chip-btn obd-financing__toolbar-products-btn"
                    data-obd-guide="commerce-products"
                    onClick={() => setCommerceHubProductsOpen(true)}
                >
                    <Cube size={16} weight="bold" aria-hidden />
                    <span className="obd-financing__products-label">Products</span>
                </button>
                {allPricingRaw && allPricingRaw.length > 0 && (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn obd-financing__view-all-btn"
                        onClick={() => setViewAllOpen(true)}
                    >
                        <ListBullets size={16} weight="bold" aria-hidden />
                        <span className="obd-financing__view-all-label">View all pricing options</span>
                    </button>
                )}
                {onRefreshRequested && (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn obd-financing__refresh-btn obd-financing__refresh-btn--toolbar"
                        title="Refresh Pricing"
                        aria-label="Refresh Pricing"
                        onClick={() => onRefreshRequested()}
                    >
                        <span className="obd-financing__refresh-label">Refresh Pricing</span>
                    </button>
                )}
            </div>
        ) : null;

    const openGenerateQuoteModel = async (
        row: FinancePartnerRow,
        col: FinancingProduct,
        syncTarget?: QuoteSyncTarget,
    ) => {
        // Runs before the sync gate: no amount of syncing makes a cross-partner combo quotable.
        if (combinedColumnPartnerMismatch(row.partner, col)) {
            showFinancePartnerMismatchModal(theme);
            return;
        }
        if (
            !assertCanGenerateQuote(
                syncTarget ?? {
                    productTypeName: productTypeFromFinancingProduct(col),
                    gridPartner: row.partner,
                    productTypes: atomicProductTypesFromFinancingProduct(col),
                },
            )
        ) {
            return;
        }
        if (!assertDesignLossesCompatibleForQuote(col)) return;
        if (col == "Solar" || col == "Roof" || col == "Battery") {
            const product = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === col));
            const activeProducts = (opportunityDetails.OpportunityProduct.filter((ele: any) => ele.ActiveFlag));
            if (product) {
                if(activeProducts.length > 1){
                    const solarLRProduct = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === "Solar" && ele.Finance_Partner_Display_Name.toLowerCase() == 'lightreach' && ele.ActiveFlag ));
                    const BatteryLRProduct = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === "Battery" && ele.Finance_Partner_Display_Name.toLowerCase() == 'lightreach' && ele.ActiveFlag ));
                    if(col ==='Solar' && solarLRProduct && solarLRProduct.Finance_Partner_Display_Name.toLowerCase() != row.partner.toLowerCase()){
                        showFinancePartnerMismatchModal(theme);
                        return;
                    }
                    if (col !== 'Roof' && solarLRProduct && BatteryLRProduct) {
                        const lrSingleQuoteOnly = lightreachSolarBatteryRequireSingleQuote(
                            solarLRProduct.Finance_Method_Name ?? solarLRProduct.finance_Method_Name,
                            BatteryLRProduct.Finance_Method_Name ?? BatteryLRProduct.finance_Method_Name,
                        );
                        if (lrSingleQuoteOnly) {
                            showInvalidQuoteCombinationModal(
                                `If the finance partner of Solar and Battery are Lightreach with the same method (PPA or Lease), ${col} single quote can't be created`,
                                theme,
                            );
                            return;
                        }
                    }

                    // Lease/Loan/PPA Storage on Solar requires Battery in the quote — all partners.
                    const solarStorageProduct = opportunityDetails.OpportunityProduct.find(
                        (ele: any) => ele.Product_Type_Name === 'Solar' && ele.ActiveFlag,
                    );
                    const batteryForStorage = opportunityDetails.OpportunityProduct.find(
                        (ele: any) => ele.Product_Type_Name === 'Battery' && ele.ActiveFlag,
                    );
                    if (
                        col !== 'Roof' &&
                        solarStorageProduct &&
                        batteryForStorage &&
                        isStorageFinanceMethod(solarStorageProduct.Finance_Method_Name)
                    ) {
                        showInvalidQuoteCombinationModal(
                            `Solar is set to ${solarStorageProduct.Finance_Method_Name}. Storage methods (Lease Storage, Loan Storage, PPA Storage) require Battery — ${col} single quote can't be created.`,
                            theme,
                        );
                        return;
                    }

                }
                if (product.Finance_Partner_Display_Name === 'Good Leap') product.Finance_Partner_Display_Name = 'GoodLeap';
                if (normalizePartner(product.Finance_Partner_Display_Name) == row.partner) {
                    const priceCell = row.prices[col];
                    const isCash = row.partner === 'Cash';

                    if(!isCash && !!product.Finance_Method_Name 
                        && (row.prices[product.Product_Type_Name] as any).financeMethod !== product.Finance_Method_Name){
                        showFinanceMethodMismatchModal(theme);
                        return;
                    }
                    if (partnerRequiresQuoteSyncChecklist(row.partner)) {
                        const currentOppId = useOneButtonStore.getState().opportunityID;
                        const syncRes = await quoteServices.getSyncStatusList(String(currentOppId));
                        const syncList = syncRes?.FinancePartnerSyncStatuses || (Array.isArray(syncRes) ? syncRes : []);
                        if (Array.isArray(syncList)) {
                            let haveAnyIGSLightreach = false;
                            opportunityDetails.OpportunityProduct.forEach((opportunityProduct: any) => {
                                if (((opportunityProduct.Finance_Partner_Display_Name == 'IGS' && row.partner === 'IGS')  || (opportunityProduct.Finance_Partner_Display_Name.toLowerCase() == 'lightreach' && row.partner.toLowerCase() === 'lightreach')) && opportunityProduct.ActiveFlag) {
                                    haveAnyIGSLightreach = true;
                                }
                            });
                            if ((haveAnyIGSLightreach &&  syncList.length > 0) ) {
                                const statusObj = syncList[0];
                                const newStatus: Partial<PartnerSyncChecklist> = {
                                    lead: statusObj.IsLeadCreate === true,
                                    contact: statusObj.IsContactSync === true,
                                    titleCheck: (statusObj.IsTilte === true || statusObj.IsTitle === true),
                                    utility: statusObj.IsUtility === true,
                                    leadSystem: statusObj.IsLeadSystem === true,
                                    creditCheck: statusObj.IsCreditPass === true,
                                };

                                if(newStatus.lead === false || newStatus.contact === false  || newStatus.utility === false || newStatus.leadSystem === false){
                                    showFinancePartnerSyncRequiredModal(theme);
                                    return;
                                }
                            }

                        } else {
                            showFinancePartnerSyncRequiredModal(theme);
                            return;
                        }
                    }

                    const data: selectedQuote = {
                        product: col,
                        partner: row.partner,
                        methord: priceCell?.financeMethod || '',
                        price: priceCell?.purchasePrice || null,
                        ExcelOffer: priceCell?.ExcelOffer || null,
                        payload: priceCell?.payload || null,
                        term: priceCell?.term,
                        apr: priceCell?.apr,
                        escalator: priceCell?.escalator ?? null,
                        tier:priceCell?.tier,
                        Recommended: priceCell?.apiRecommended === true,
                    };
                    setConfirmQuoteCellKey(data);


                } else {
                    showFinancePartnerMismatchModal(theme);
                }
            } else {
                showProductNotSelectedModal(theme);
            }
        } else if (col == "Solar + Battery") {
            const product = (opportunityDetails.OpportunityProduct.filter((ele: any) => ((ele.Product_Type_Name === "Solar" || ele.Product_Type_Name ===  "Battery") && ele.ActiveFlag)));
            if (product.length > 1) {
                let Finance_Partner = product[0].Finance_Partner_Display_Name;
                if (Finance_Partner === 'Good Leap') Finance_Partner = 'GoodLeap';
                if (normalizePartner(Finance_Partner) == row.partner && product[0].Finance_Partner_Display_Name == product[1].Finance_Partner_Display_Name) {
                   
                    const solarProduct = product.find((ele: any) => ele.Product_Type_Name === "Solar");
                    let solarFinance_Method_Name= solarProduct?.Finance_Method_Name;

                    const batteryProduct = product.find((ele: any) => ele.Product_Type_Name === "Battery");
                    let batteryFinance_Method_Name = batteryProduct?.Finance_Method_Name;
                    const isCash = row.partner === 'Cash';
                    if(!isCash && (solarFinance_Method_Name !== batteryFinance_Method_Name || row?.prices[col]?.financeMethod !== batteryFinance_Method_Name || row?.prices[col]?.financeMethod !== solarFinance_Method_Name)){
                        showFinanceMethodMismatchedModal(theme);
                        return;
                    }
                   
                    const data: selectedQuote = {
                        product: col,
                        products: []
                    }
                    product.forEach((ele: any) => {
                        const priceCell = row.prices[col];
                        let financeMethod = priceCell?.financeMethod;
                        let ExcelOffer = priceCell?.ExcelOffer;
                        if (ele.Product_Type_Name === "Battery" && priceCell?.financeMethod === "Loan") {
                            financeMethod = "Battery Loan"
                        }
                        if(ele.Product_Type_Name === "Battery"){
                            ExcelOffer = row.prices.Solar?.ExcelOffer;
                        }
                        data.products!.push({
                            product: ele.Product_Type_Name,
                            partner: ele.Finance_Partner_Display_Name,
                            methord: financeMethod || '',
                            price: priceCell?.purchasePrice || null,
                            ExcelOffer: ExcelOffer || null,
                            payload: priceCell?.payload || null,
                            term: priceCell?.term,
                            apr: priceCell?.apr,
                            escalator: priceCell?.escalator ?? null,
                            tier: priceCell?.tier,
                            Recommended: priceCell?.apiRecommended === true
                        })
                    })
                    setConfirmQuoteCellKey(data);
                } else {
                    showFinancePartnerMismatchModal(theme);
                }

            } else {
                showProductNotSelectedModal(theme);
            }
        } else if(col == "Solar + Roof"){
            const product = (opportunityDetails.OpportunityProduct.filter((ele: any) => ((ele.Product_Type_Name === "Solar" || ele.Product_Type_Name ===  "Roof")) && ele.ActiveFlag));
            if (product.length > 1) {
                let Finance_Partner = product[0].Finance_Partner_Display_Name;
                if (Finance_Partner === 'Good Leap') Finance_Partner = 'GoodLeap';
                if (normalizePartner(Finance_Partner) == row.partner && product[0].Finance_Partner_Display_Name == product[1].Finance_Partner_Display_Name) {
                    
                    const solarProduct = product.find((ele: any) => ele.Product_Type_Name === "Solar");
                    let solarFinance_Method_Name= solarProduct?.Finance_Method_Name;

                    const roofProduct = product.find((ele: any) => ele.Product_Type_Name === "Roof");
                    let roofFinance_Method_Name = roofProduct?.Finance_Method_Name;
                    const isCash = row.partner === 'Cash';
                    if(!isCash && (solarFinance_Method_Name !== roofFinance_Method_Name || row?.prices[col]?.financeMethod !== roofFinance_Method_Name || row?.prices[col]?.financeMethod !== solarFinance_Method_Name)){
                        showFinanceMethodMismatchedModal(theme);
                        return;
                    }
                    
                    const data: selectedQuote = {
                        product: col,
                        products: []
                    }
                    product.forEach((ele: any) => {
                        const priceCell = row.prices[col];
                        let financeMethod = priceCell?.financeMethod;
                        let ExcelOffer = priceCell?.ExcelOffer;
                        data.products!.push({
                            product: ele.Product_Type_Name,
                            partner: ele.Finance_Partner_Display_Name,
                            methord: financeMethod || '',
                            price: priceCell?.purchasePrice || null,
                            ExcelOffer: ExcelOffer || null,
                            payload: priceCell?.payload || null,
                            term: priceCell?.term,
                            apr: priceCell?.apr,
                            escalator: priceCell?.escalator ?? null,
                            tier: priceCell?.tier,
                            Recommended: priceCell?.apiRecommended === true
                        })
                    })
                    setConfirmQuoteCellKey(data);
                } else {
                    showFinancePartnerMismatchModal(theme);
                }

            } else {
                showProductNotSelectedModal(theme);
            }
        } else if( col == "Battery + Roof"){
            const product = (opportunityDetails.OpportunityProduct.filter((ele: any) => ((ele.Product_Type_Name === "Roof" || ele.Product_Type_Name ===  "Battery") && ele.ActiveFlag)));

            if (product.length > 1) {
                let Finance_Partner = product[0].Finance_Partner_Display_Name;
                if (Finance_Partner === 'Good Leap') Finance_Partner = 'GoodLeap';
                if (normalizePartner(Finance_Partner) == row.partner && product[0].Finance_Partner_Display_Name == product[1].Finance_Partner_Display_Name) {
                    const priceCell = row.prices[col];
                    const data: selectedQuote = {
                        product: col,
                        products: []
                    }
                    product.forEach((ele: any) => {
                        const priceCell = row.prices[ele.Product_Type_Name];
                        let financeMethod = priceCell?.financeMethod;

                        if (ele.Product_Type_Name === "Battery" && priceCell?.financeMethod === "Loan") {
                            financeMethod = "Battery Loan"
                        }
                        data.products!.push({
                            product: ele.Product_Type_Name,
                            partner: ele.Finance_Partner_Display_Name,
                            methord: financeMethod || '',
                            price: priceCell?.purchasePrice || null,
                            ExcelOffer: priceCell?.ExcelOffer || null,
                            payload: priceCell?.payload || null,
                            term: priceCell?.term,
                            apr: priceCell?.apr,
                            escalator: priceCell?.escalator ?? null,
                            tier: priceCell?.tier,
                            Recommended: priceCell?.apiRecommended === true
                        })
                    })
                    setConfirmQuoteCellKey(data);
                } else {
                    showFinancePartnerMismatchModal(theme);
                }
            } else {
                showProductNotSelectedModal(theme);
            }
        }
    };

    const openGenerateQuoteFromCombinedSummary = async (
        summary: CombinedFinancingSummary,
        productTypesOverride?: Array<'Solar' | 'Roof' | 'Battery'>,
        syncTarget?: QuoteSyncTarget,
    ) => {
        const combinedScope = buildQuoteSyncScopeFromCombinedSummary(summary, productTypesOverride);
        const effectiveSyncTarget =
            syncTarget ??
            ({
                productTypeName: combinedScope.quoteProductTypes?.[0] ?? 'Solar',
                productTypes: combinedScope.quoteProductTypes,
                quotePartners: combinedScope.quotePartners,
            } satisfies QuoteSyncTarget);
        if (!assertCanGenerateQuote(effectiveSyncTarget)) return;
        const coveredTypes =
            productTypesOverride && productTypesOverride.length > 0
                ? productTypesOverride
                : atomicProductTypesFromCombinedSummary(summary);
        if (
            coveredTypes.includes('Solar') &&
            !assertDesignLossesCompatibleForQuote('Solar')
        ) {
            return;
        }
        if (!summary.lines.length) {
            showNoRecommendedPricingModal(theme);
            return;
        }

        const picks = summary.lines.flatMap((line) => line.picks);
        const distinctPartners = new Set(summary.lines.map((line) => line.partner));
        // Single grid cell path only when one partner and one atomic product — never for
        // cross-partner combos (e.g. GoodLeap Roof + LightReach Solar + Battery).
        const canUseSingleGridCellPath =
            summary.lines.length <= 1 &&
            distinctPartners.size <= 1 &&
            picks.length === 1 &&
            coveredTypes.length <= 1;
        if (canUseSingleGridCellPath) {
            const pick = picks[0];
            const row = displayGridRows.find(
                (r) => normalizePartner(r.partner) === normalizePartner(pick.partner),
            );
            if (row) {
                await openGenerateQuoteModel(row, pick.product);
                return;
            }
        }

        const matchIssues = findCombinedSummaryOpportunityMismatches(
            summary,
            opportunityDetails,
            coveredTypes,
        );
        if (matchIssues.length > 0) {
            const hasPartnerIssue = matchIssues.some((i) => i.reason === 'partner' || i.reason === 'missing');
            setQuoteMatchPopup({
                title: hasPartnerIssue ? 'Partner and product must match' : 'Finance method mismatch',
                intro: 'Update product finance settings to match this plan before generating a quote.',
                issues: matchIssues,
            });
            return;
        }

        const needsQuoteSyncChecklist = picks.some((pick) => partnerRequiresQuoteSyncChecklist(pick.partner));
        if (needsQuoteSyncChecklist) {
            const currentOppId = useOneButtonStore.getState().opportunityID;
            const syncRes = await quoteServices.getSyncStatusList(String(currentOppId));
            const syncList = syncRes?.FinancePartnerSyncStatuses || (Array.isArray(syncRes) ? syncRes : []);
            if (Array.isArray(syncList) && syncList.length > 0) {
                let haveAnyIGSLightreach = false;
                (opportunityDetails?.OpportunityProduct ?? []).forEach((opportunityProduct: any) => {
                    if (
                        (opportunityProduct.Finance_Partner_Display_Name == 'IGS' ||
                            opportunityProduct.Finance_Partner_Display_Name.toLowerCase() == 'lightreach') &&
                        opportunityProduct.ActiveFlag
                    ) {
                        haveAnyIGSLightreach = true;
                    }
                });
                if (haveAnyIGSLightreach) {
                    const statusObj = syncList[0];
                    const newStatus: Partial<PartnerSyncChecklist> = {
                        lead: statusObj.IsLeadCreate === true,
                        contact: statusObj.IsContactSync === true,
                        titleCheck: statusObj.IsTilte === true || statusObj.IsTitle === true,
                        utility: statusObj.IsUtility === true,
                        leadSystem: statusObj.IsLeadSystem === true,
                        creditCheck: statusObj.IsCreditPass === true,
                    };

                    if (
                        newStatus.lead === false ||
                        newStatus.contact === false ||
                        newStatus.utility === false ||
                        newStatus.leadSystem === false
                    ) {
                        showFinancePartnerSyncRequiredModal(theme);
                        return;
                    }
                }
            } else {
                showFinancePartnerSyncRequiredModal(theme);
                return;
            }
        }

        const data =
            productTypesOverride && productTypesOverride.length > 0
                ? buildSelectedQuoteFromOpportunityProductTypes(
                      productTypesOverride,
                      opportunityDetails,
                      displayGridRows,
                      summary,
                  )
                : buildSelectedQuoteFromCombinedSummary(summary, displayGridRows, opportunityDetails);
        if (!data) {
            showUnableToOpenQuoteFormModal(theme);
            return;
        }
        setConfirmQuoteCellKey(data);
    };

    const openGenerateQuoteFromSelectedSummary = async (details: any) => {
        const syncTarget: QuoteSyncTarget = {
            productTypeName: productTypeFromFinancingProduct(details.product),
            gridPartner: details.partner,
            productTypes: atomicProductTypesFromFinancingProduct(details.product),
        };
        const row: FinancePartnerRow | undefined = displayGridRows.find(
            (ele: FinancePartnerRow) => ele.partner == details.partner,
        );
        if (row) openGenerateQuoteModel(row, details.product, syncTarget);
    };

    const openGenerateQuoteFromAiPlan = async (
        plan: FinancingAiPlan,
        context?: FinancingAiQuoteContext,
    ) => {
        const includedLineIds = context?.includedLineIds ?? [];
        const coveredTypes = (context?.coveredProducts ?? []).filter(
            (p): p is 'Solar' | 'Roof' | 'Battery' => p === 'Solar' || p === 'Roof' || p === 'Battery',
        );
        const isHeroCombinedQuote = context?.quoteScope === 'combined';
        const filteredSummary =
            includedLineIds.length > 0
                ? filterCombinedSummaryByLineIds(combinedFinancingSummary, includedLineIds)
                : combinedFinancingSummary;
        const summaryForQuote = filteredSummary.lines.length
            ? filteredSummary
            : combinedFinancingSummary;

        if (isHeroCombinedQuote) {
            const combinedScope = buildQuoteSyncScopeFromCombinedSummary(
                summaryForQuote,
                coveredTypes.length > 0 ? coveredTypes : undefined,
            );
            const syncTarget: QuoteSyncTarget = {
                productTypeName: combinedScope.quoteProductTypes?.[0] ?? 'Solar',
                productTypes: combinedScope.quoteProductTypes,
                quotePartners: combinedScope.quotePartners,
            };
            if (!assertCanGenerateQuote(syncTarget)) return;
            await openGenerateQuoteFromCombinedSummary(
                summaryForQuote,
                coveredTypes.length > 0 ? coveredTypes : undefined,
                syncTarget,
            );
            return;
        }

        const quoteCombined =
            context?.quoteScope !== 'plan' && !plan.isCash && coveredTypes.length > 1;

        let syncTarget: QuoteSyncTarget;
        if (quoteCombined && !plan.isCash) {
            const combinedScope = buildQuoteSyncScopeFromCombinedSummary(
                summaryForQuote,
                coveredTypes.length > 0 ? coveredTypes : undefined,
            );
            syncTarget = {
                productTypeName: combinedScope.quoteProductTypes?.[0] ?? 'Solar',
                productTypes: combinedScope.quoteProductTypes,
                quotePartners: combinedScope.quotePartners,
            };
        } else {
            syncTarget = {
                productTypeName: productTypeFromFinancingProduct(plan.product),
                gridPartner: plan.partner,
                productTypes: atomicProductTypesFromFinancingProduct(plan.product),
            };
        }

        if (!assertCanGenerateQuote(syncTarget)) return;

        if (quoteCombined && !plan.isCash) {
            await openGenerateQuoteFromCombinedSummary(
                summaryForQuote,
                coveredTypes.length > 0 ? coveredTypes : undefined,
                syncTarget,
            );
            return;
        }

        const row = displayGridRows.find((r) => r.partner === plan.partner);
        if (!row) {
            showPricingNotFoundModal(theme);
            return;
        }
        await openGenerateQuoteModel(row, plan.product);
    };

    const updateQuoteVersion = () => {
        setConfirmQuoteCellKey(null);
        setQuotesRefreshTrigger(Date.now());
        setCommerceHubQuotesOpen(true);
    }

    const obdTheme = theme === 'dark' ? 'dark' : 'light';

    return (
        <div className={`obd-financing-grid-root obd-theme-${obdTheme}`}>
            {noticeContextHolder}

            <div className={`obd-card obd-card--financing${cardMod}`}>
                {!embeddedInPricingModal && (
                    <div
                        role="button"
                        tabIndex={0}
                        className="obd-quotes__header"
                        onClick={() => setSectionOpen((o) => !o)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSectionOpen((o) => !o); } }}
                        style={{ width: '100%' }}
                    >
                        <span className="obd-card__label obd-card__label--section-icon">
                            <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                                <CurrencyCircleDollar size={16} weight="duotone" />
                            </span>
                            Financing Options for the Finalized Design
                        </span>
                        <div className="obd-financing__header-end">
                            {pricingBusy ? (
                                <span
                                    className="obd-financing__header-spinner"
                                    role="status"
                                    aria-live="polite"
                                    aria-label="Loading pricing"
                                    title="Loading pricing…"
                                >
                                    <Spin size="small" />
                                </span>
                            ) : null}
                            {(combinedFinancingSummary.totalMonthly > 0 || pricingBusy) ? (
                                <FinancingDealHealthChip
                                    monthlyDisplay={combinedMonthlyDisplay}
                                    className="obd-financing-combined__deal-health--header"
                                />
                            ) : null}
                            {aiPlansReady ? (
                                <span className="obd-financing__ai-ready-badge">
                                    <Sparkle size={12} weight="fill" aria-hidden />
                                    AI plans ready
                                </span>
                            ) : null}
                            <div className="obd-financing__header-actions">
                            {onRefreshRequested && !sectionOpen && (
                                <button
                                    type="button"
                                    className="obd-utility__edit-btn obd-financing__refresh-btn"
                                    title="Refresh pricing"
                                    aria-label="Refresh pricing"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRefreshRequested();
                                    }}
                                >
                                    <ArrowsClockwise size={16} weight="regular" />
                                </button>
                            )}
                            <CaretDown size={14} weight="bold" className={`obd-quotes__chevron ${sectionOpen ? 'obd-quotes__chevron--open' : ''}`} />
                            </div>
                        </div>
                    </div>
                )}
                {contentOpen && (
                    <div className={`obd-financing__wrap${wrapMod}`}>
                        {embeddedInPricingModal ? (
                            <div className="obd-financing__modal-summary">
                                {systemHeaderBlock}
                                {toolbarBlock}
                            </div>
                        ) : (
                            <>
                                <div className="obd-financing__summary-row">
                                    {systemHeaderBlock}
                                    {toolbarBlock}
                                </div>
                                {pricingFallbackFlags.roof || pricingFallbackFlags.battery ? (
                                    <div className="obd-financing__fallback-flags" role="status">
                                        {pricingFallbackFlags.roof ? (
                                            <span className="obd-financing__system-tile-badge">
                                                {pricingFallbackFlags.roof}
                                            </span>
                                        ) : null}
                                        {pricingFallbackFlags.battery ? (
                                            <span className="obd-financing__system-tile-badge">
                                                {pricingFallbackFlags.battery}
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null}
                                {loadError && (
                                    <div className="obd-financing__empty obd-financing__empty--compact obd-financing__api-error-banner" role="alert">
                                        <span className="obd-financing__load-error-text">{loadError}</span>
                                    </div>
                                )}
                                {!loadError && financingGrid.length === 0 && (
                                    <div
                                        className="obd-financing__empty"
                                        style={{ marginBottom: 8, fontSize: 12, color: 'rgba(148,163,184,0.9)' }}
                                        role="status"
                                        aria-live="polite"
                                    >
                                        {pricingBusy ? (
                                            <>
                                                <Spin size="small" style={{ marginRight: 8 }} />
                                                Loading pricing…
                                            </>
                                        ) : (
                                            'Click the $ button on a design to load financing options.'
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                        {embeddedInPricingModal && (pricingFallbackFlags.roof || pricingFallbackFlags.battery) ? (
                            <div className="obd-financing__fallback-flags" role="status">
                                {pricingFallbackFlags.roof ? (
                                    <span className="obd-financing__system-tile-badge">{pricingFallbackFlags.roof}</span>
                                ) : null}
                                {pricingFallbackFlags.battery ? (
                                    <span className="obd-financing__system-tile-badge">{pricingFallbackFlags.battery}</span>
                                ) : null}
                            </div>
                        ) : null}
                        {embeddedInPricingModal && loadError && (
                            <div className="obd-financing__empty obd-financing__empty--compact obd-financing__api-error-banner" role="alert">
                                <span className="obd-financing__load-error-text">{loadError}</span>
                            </div>
                        )}
                        {embeddedInPricingModal && !loadError && financingGrid.length === 0 && (
                            <div
                                className="obd-financing__empty"
                                style={{ marginBottom: 8, fontSize: 12, color: 'rgba(148,163,184,0.9)' }}
                                role="status"
                                aria-live="polite"
                            >
                                {pricingBusy ? (
                                    <>
                                        <Spin size="small" style={{ marginRight: 8 }} />
                                        Loading pricing…
                                    </>
                                ) : (
                                    'Click the $ button on a design to load financing options.'
                                )}
                            </div>
                        )}
                        <div
                            className="obd-financing__view-tabs"
                            role="tablist"
                            aria-label="Financing view"
                        >
                            <button
                                type="button"
                                role="tab"
                                id="obd-financing-view-tab-ai-plans"
                                aria-selected={financingView === 'ai-plans'}
                                aria-controls="obd-financing-view-panel-ai-plans"
                                className={`obd-financing__view-tab${financingView === 'ai-plans' ? ' obd-financing__view-tab--active' : ''}`}
                                onClick={() => setFinancingView('ai-plans')}
                            >
                                Financing options
                            </button>
                            <button
                                type="button"
                                role="tab"
                                id="obd-financing-view-tab-combined"
                                aria-selected={financingView === 'combined'}
                                aria-controls="obd-financing-view-panel-combined"
                                className={`obd-financing__view-tab${financingView === 'combined' ? ' obd-financing__view-tab--active' : ''}`}
                                onClick={() => setFinancingView('combined')}
                            >
                                Combined financing
                            </button>
                            <button
                                type="button"
                                role="tab"
                                id="obd-financing-view-tab-grid"
                                aria-selected={financingView === 'grid'}
                                aria-controls="obd-financing-view-panel-grid"
                                className={`obd-financing__view-tab${financingView === 'grid' ? ' obd-financing__view-tab--active' : ''}`}
                                onClick={() => setFinancingView('grid')}
                            >
                                Best pricing options
                            </button>
                        </div>
                        {financingView === 'ai-plans' ? (
                            <div
                                id="obd-financing-view-panel-ai-plans"
                                role="tabpanel"
                                aria-labelledby="obd-financing-view-tab-ai-plans"
                            >
                                <FinancingAiPlansView
                                    plans={financingAiPlans}
                                    combinedSummary={combinedFinancingSummary}
                                    loading={pricingBusy}
                                    selectedCellKeys={selectedFinanceCells}
                                    onRefreshAi={onRefreshRequested}
                                    onGenerateQuote={(plan, context) =>
                                        void openGenerateQuoteFromAiPlan(plan, context)
                                    }
                                    generateQuoteDisabled={isGenerateQuotePermissionDisabled}
                                    getQuoteDesignBlockedReasonForPlan={(plan) =>
                                        quoteDesignBlockedReasonForProducts(
                                            atomicProductTypesFromFinancingProduct(plan.product),
                                        )
                                    }
                                    onQuoteDesignBlocked={notifyQuoteDesignBlocked}
                                    getQuoteSyncBlockedHint={(plan) =>
                                        quoteSyncBlockedHintForPartnerProduct(
                                            plan.partner,
                                            atomicProductTypesFromFinancingProduct(plan.product),
                                        )
                                    }
                                    quoteSyncBlockedHintForScope={quoteSyncBlockedHintForScope}
                                />
                            </div>
                        ) : financingView === 'combined' ? (
                            <div
                                id="obd-financing-view-panel-combined"
                                role="tabpanel"
                                aria-labelledby="obd-financing-view-tab-combined"
                            >
                                <FinancingCombinedView
                                    summary={combinedFinancingSummary}
                                    loading={pricingBusy}
                                    onOpenGenerateQuote={() => {
                                        const coveredTypes =
                                            atomicProductTypesFromCombinedSummary(combinedFinancingSummary);
                                        const combinedScope = buildQuoteSyncScopeFromCombinedSummary(
                                            combinedFinancingSummary,
                                        );
                                        const syncTarget = combinedFinancingSummary.lines.length
                                            ? {
                                                  productTypeName: coveredTypes[0] ?? 'Solar',
                                                  productTypes: combinedScope.quoteProductTypes,
                                                  quotePartners: combinedScope.quotePartners,
                                              }
                                            : undefined;
                                        void openGenerateQuoteFromCombinedSummary(
                                            combinedFinancingSummary,
                                            undefined,
                                            syncTarget,
                                        );
                                    }}
                                    onOpenSelectedGenerateQuote={(event: any) =>
                                        openGenerateQuoteFromSelectedSummary(event)
                                    }
                                    generateQuoteDisabled={isGenerateQuotePermissionDisabled}
                                    getQuoteDesignBlockedReason={() =>
                                        quoteDesignBlockedReasonForProducts(
                                            atomicProductTypesFromCombinedSummary(combinedFinancingSummary),
                                        )
                                    }
                                    getQuoteDesignBlockedReasonForLine={(line) =>
                                        quoteDesignBlockedReasonForProducts([
                                            ...new Set(
                                                line.picks.flatMap((pick) =>
                                                    atomicProductTypesFromFinancingProduct(pick.product),
                                                ),
                                            ),
                                        ])
                                    }
                                    onQuoteDesignBlocked={notifyQuoteDesignBlocked}
                                    getSummaryQuoteSyncBlockedHint={() =>
                                        quoteSyncBlockedHintForScope(
                                            buildQuoteSyncScopeFromCombinedSummary(combinedFinancingSummary),
                                        )
                                    }
                                    getLineQuoteSyncBlockedHint={(line) =>
                                        quoteSyncBlockedHintForPartnerProduct(
                                            line.partner,
                                            [
                                                ...new Set(
                                                    line.picks.flatMap((pick) =>
                                                        atomicProductTypesFromFinancingProduct(pick.product),
                                                    ),
                                                ),
                                            ],
                                        )
                                    }
                                />
                            </div>
                        ) : (
                        <div
                            id="obd-financing-view-panel-grid"
                            role="tabpanel"
                            aria-labelledby="obd-financing-view-tab-grid"
                            className="obd-financing__table-container"
                        >
                            <table className="obd-financing__table">
                            <thead>
                                <tr>
                                    <th className="obd-financing__corner" scope="col">PARTNER</th>
                                    {/* {showTreeCutCostColumn && (
                                        <th className="obd-financing__col-header obd-financing__col-header--num" scope="col">
                                            TREE
                                        </th>
                                    )} */}
                                    {FINANCING_PRODUCTS.map((col) => (
                                        <th key={col} data-product={col} className="obd-financing__col-header" scope="col">
                                            {col.toUpperCase()}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayGridRows.map((row) => {
                                    const isCash = row.partner === 'Cash';
                                    return (
                                        <tr key={row.partner} className={isCash ? 'obd-financing__row--cash' : ''}>
                                            <td className="obd-financing__partner">{partnerLabelForGrid(row.partner)}</td>
                                            {FINANCING_PRODUCTS.map((col) => {
                                                const cell = row.prices[col];
                                                const cellKey = `${row.partner}::${col}`;
                                                const isSelected = selectedFinanceCells.has(cellKey);
                                                const cellProductTypes =
                                                    atomicProductTypesFromFinancingProduct(col);
                                                const cellHardBlocked = hasQuoteHardMissForProducts(
                                                    cellProductTypes,
                                                    systemDesignMissingItems,
                                                );
                                                const cellHardBlockedReason = cellHardBlocked
                                                    ? quoteBlockedReasonFromItems(
                                                          cellProductTypes,
                                                          systemDesignMissingItems,
                                                      )
                                                    : undefined;
                                                const cellPartnerMismatch =
                                                    !!cell && combinedColumnPartnerMismatch(row.partner, col);
                                                const cellSolarRate = solarRateFromOffer(cell);
                                                const cellSyncBlocked =
                                                    !!cell &&
                                                    !cellHardBlocked &&
                                                    !cellPartnerMismatch &&
                                                    isQuoteSyncBlockedForScope({
                                                        quotePartner: row.partner,
                                                        quoteProductTypes: cellProductTypes,
                                                    });
                                                const cellQuoteVisuallyBlocked =
                                                    isGenerateQuotePermissionDisabled ||
                                                    cellHardBlocked ||
                                                    cellPartnerMismatch ||
                                                    cellSyncBlocked;
                                                return (
                                                    <td
                                                        key={col}
                                                        data-product={col}
                                                        title={
                                                            cell && !cellQuoteVisuallyBlocked
                                                                ? 'Click to generate quote.'
                                                                : cellHardBlocked
                                                                  ? cellHardBlockedReason ??
                                                                    'Complete required design before generating a quote.'
                                                                  : cellPartnerMismatch
                                                                    ? `${partnerLabelForGrid(row.partner)} is not the finance partner on every product in this combination.`
                                                                    : cellSyncBlocked
                                                                      ? 'Click to open Partner Sync.'
                                                                      : undefined
                                                        }
                                                        className={`obd-financing__cell ${cell ? 'obd-financing__cell--available' : ''} ${isSelected ? 'obd-financing__cell--selected' : ''}${cell && cellQuoteVisuallyBlocked ? ' obd-financing__cell--quote-disabled' : ''}`}
                                                        onClick={(e) => {
                                                            if (e.ctrlKey || e.metaKey) {
                                                                if (cell) toggleFinanceCell(cellKey);
                                                                return;
                                                            }
                                                            if (!cell) return;
                                                            if (isGenerateQuotePermissionDisabled) return;
                                                            if (cellHardBlocked) {
                                                                showQuoteDesignBlockedModal(cellProductTypes);
                                                                return;
                                                            }
                                                            openGenerateQuoteModel(row, col);
                                                        }}
                                                    >
                                                        <span className="obd-financing__cell-inner">
                                                            {cell ? (
                                                                <>
                                                                    <span className="obd-financing__cell-content">
                                                                        {(!isCash && cell.recommended) ||
                                                                            (row.partner === 'GoodLeap' &&
                                                                                cell.recommended &&
                                                                                cell.purchasePrice != null &&
                                                                                cell.purchasePrice > 0) ? (
                                                                            <span className="obd-financing__cell-stack obd-financing__cell-stack--meta">
                                                                                {!isCash && cell.recommended && (
                                                                                    <span
                                                                                        className="obd-financing__recommended"
                                                                                        role="img"
                                                                                        aria-label="Recommended"
                                                                                        title="Recommended"
                                                                                    >
                                                                                        <Star size={15} weight="fill" className="obd-financing__recommended-icon" aria-hidden />
                                                                                    </span>
                                                                                )}
                                                                                {row.partner === 'GoodLeap' &&
                                                                                    cell.recommended &&
                                                                                    cell.purchasePrice != null &&
                                                                                    cell.purchasePrice > 0 && (
                                                                                        <span className="obd-financing__purchase">
                                                                                            Purchase{' '}
                                                                                            <span className="obd-financing__purchase-amt">
                                                                                                $
                                                                                                {cell.purchasePrice.toLocaleString(undefined, {
                                                                                                    maximumFractionDigits: 0,
                                                                                                })}
                                                                                            </span>
                                                                                        </span>
                                                                                    )}
                                                                            </span>
                                                                        ) : null}
                                                                        <span className="obd-financing__cell-stack obd-financing__cell-stack--payment">
                                                                            {!isCash && cell.financeMethod && (
                                                                                (cell.financeMethod.toLowerCase().includes('prod') ||
                                                                                    cell.financeMethod.toLowerCase().includes('guarantee'))
                                                                                    ? (
                                                                                        <Tooltip title={TOOLTIP_PROD_GUARANTEE}>
                                                                                            <span className="obd-financing__method">{cell.financeMethod}</span>
                                                                                        </Tooltip>
                                                                                    )
                                                                                    : <span className="obd-financing__method">{cell.financeMethod}</span>
                                                                            )}
                                                                            <span className="obd-financing__price">
                                                                                <span className="obd-financing__price-amount">
                                                                                    <span className="obd-financing__dollar">$</span>
                                                                                    {cell.monthly.toLocaleString()}
                                                                                </span>
                                                                                {isCash ? (
                                                                                    <span className="obd-financing__meta obd-financing__meta--period">one-time</span>
                                                                                ) : (
                                                                                    <span className="obd-financing__meta obd-financing__meta--period">/mo</span>
                                                                                )}
                                                                            </span>
                                                                        </span>
                                                                        {!isCash && (cell.apr > 0 || cell.term > 0 || cell.escalator != null) && (
                                                                            <span className="obd-financing__cell-stack obd-financing__cell-stack--terms">
                                                                                <span className="obd-financing__details">
                                                                                    {cell.apr != null && cell.apr > 0 ? (
                                                                                        <span className="obd-financing__apr">{cell.apr}% APR</span>
                                                                                    ) : cell.escalator != null ? (
                                                                                        <Tooltip title={TOOLTIP_ESCALATOR}>
                                                                                            <span className="obd-financing__apr obd-financing__apr--with-tooltip">{cell.escalator}% escalator</span>
                                                                                        </Tooltip>
                                                                                    ) : null}
                                                                                    {cell.term > 0 && <span className="obd-financing__term">{cell.term} yr</span>}
                                                                                </span>
                                                                            </span>
                                                                        )}
                                                                        {!isCash && cellSolarRate != null && (
                                                                            <span className="obd-financing__cell-stack obd-financing__cell-stack--rate">
                                                                                <span className="obd-financing__rate" title="Solar rate (starting $/kWh)">
                                                                                    {formatSolarRatePerKwh(cellSolarRate)}
                                                                                </span>
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                    {isSelected && <Check size={14} weight="bold" className="obd-financing__cell-check" aria-hidden />}
                                                                </>
                                                            ) : (
                                                                <span className="obd-financing__na" title="Not available">
                                                                    {FINANCING_CELL_NA_LABEL}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                            </table>
                        </div>
                        )}
                    </div>
                )}
                {allPricingRaw && (
                    <DashboardModal
                        open={viewAllOpen}
                        onClose={() => setViewAllOpen(false)}
                        title="All pricing options"
                        width="min(96vw, 1320px)"
                        theme={theme}
                        className="obd-pricing-viewall-modal"
                        rootClassName={`obd-theme-${theme} obd-pricing-viewall-root--bento`}
                    >
                        <div className="obd-pricing-viewall">
                            <p className="obd-pricing-viewall__sub">Recommended options show a star in the status column. Select a layout above to improve pricing availability.</p>
                            <div className="obd-pricing-viewall__metrics" style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--theme-text-sec, #888)' }}>
                                <span><strong>System size:</strong> {systemSizeDisplay}</span>
                                <span><strong>Production:</strong> {productionDisplay.value}</span>
                                <span><strong>Multiplier:</strong> {multiplierDisplay}</span>
                            </div>
                            <div className="obd-pricing-viewall__table-wrap">
                                <table className="obd-pricing-viewall__table">
                                    <thead>
                                        <tr>
                                            <th className="obd-pricing-viewall__th">Tree details</th>
                                            <th className="obd-pricing-viewall__th">Partner</th>
                                            <th className="obd-pricing-viewall__th">Product</th>
                                            <th className="obd-pricing-viewall__th">Finance method</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Monthly</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Loan / Total</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Term</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">APR / Escalator</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Minimum Rate</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Rate Cap</th>
                                            <th className="obd-pricing-viewall__th">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {viewAllRows.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                className={`obd-pricing-viewall__tr ${row.recommended ? 'obd-pricing-viewall__tr--rec' : ''} ${row.error ? 'obd-pricing-viewall__tr--err' : ''}`}
                                            >
                                                <td className="obd-pricing-viewall__td">
                                                    {row.treeDetailsText ? row.treeDetailsText : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td">{partnerLabelForGrid(row.partner)}</td>
                                                <td className="obd-pricing-viewall__td">{row.product}</td>
                                                <td className="obd-pricing-viewall__td">{row.financeMethod}</td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.error ? '—' : row.partner === 'Cash' ? '—' : (row.monthly != null && row.monthly > 0 ? `$${row.monthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo` : '—')}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.loanAmount != null ? `$${row.loanAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.term != null ? `${row.term} yr` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.apr != null ? `${row.apr}% APR` : row.escalatorPct != null ? `${row.escalatorPct}% escalator` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.minimumRate != null ? `$${Number(row.minimumRate).toFixed(3)}` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.rateCap != null ? `$${Number(row.rateCap).toFixed(3)}` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td">
                                                    {row.error ? (
                                                        <span className="obd-pricing-viewall__err">{row.error}</span>
                                                    ) : row.recommended ? (
                                                        <span
                                                            className="obd-pricing-viewall__rec-star"
                                                            role="img"
                                                            aria-label="Recommended"
                                                            title="Recommended"
                                                        >
                                                            <Star size={16} weight="fill" className="obd-pricing-viewall__rec-star-icon" aria-hidden />
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </DashboardModal>
                )}

                <DashboardModal
                    open={!!quoteMatchPopup}
                    onClose={() => setQuoteMatchPopup(null)}
                    title={quoteMatchPopup?.title ?? 'Partner and product must match'}
                    width={520}
                    theme={theme}
                    zIndex={OBD_DASHBOARD_MODAL_Z + 80}
                    getContainer={() => document.body}
                    footer={
                        <button
                            type="button"
                            className="obd-modal__save"
                            onClick={() => setQuoteMatchPopup(null)}
                        >
                            OK
                        </button>
                    }
                >
                    <div className="obd-quote-match">
                        <p className="obd-quote-match__intro">{quoteMatchPopup?.intro}</p>
                        {quoteMatchPopup?.issues.length ? (
                            <ul className="obd-quote-match__list">
                                {quoteMatchPopup.issues.map((issue) => (
                                    <li key={`${issue.product}-${issue.reason}`} className="obd-quote-match__item">
                                        <span className="obd-quote-match__product">{issue.product}</span>
                                        <QuoteMatchIssueRows issue={issue} />
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                </DashboardModal>

                <GenerateQuoteModal
                    confirmQuoteCellKey={confirmQuoteCellKey}
                    quoteForm={quoteForm}
                    priceQuote={priceQuote}
                    setConfirmQuoteCellKey={setConfirmQuoteCellKey}
                    setQuoteForm={setQuoteForm}
                    updateQuoteVersion={updateQuoteVersion}
                    noticeApi={noticeApi}
                    rcomendedQuote={true}
                />
            </div>
        </div>
    );
};

export default FinancingGridCard;

